import React, { useState } from 'react';
import { unitRole, redundancyPolicy, isGalleryPage } from '../config/adUnitTaxonomy';
import { attachOmidSessions } from '../validation/omidJoin';
import { attachActiveView, isActiveViewRow } from '../validation/activeView';
import { guardIssues } from '../validation/usbGuard';
import { tierFor } from '../validation/lifecycleConfidence';
import { buildMediationPhase } from '../validation/mediationLifecycle';
import { fillResultForGam, fillResultIssue, isFillResultRow } from '../validation/mediationFillResult';
import { metaTestModeIssue } from '../validation/metaTestMode';
import { metaPlacementIssues } from '../validation/metaPlacement';
import { resolveSessionMapping } from '../validation/sdkBiddingMapping';
import { getPacks } from '../api/appPacks';
import { mediationFillOf, mediationImpUrlsOf } from '../utils/gamMediation';


/* ─── timestamp helper ─────────────────────────────────────── */
// Always returns a number (ms epoch) — handles ISO strings, epoch numbers, and missing values.
const toMs = (v) => {
  if (!v && v !== 0) return 0;
  if (typeof v === 'number') return v;
  const n = Date.parse(v);
  return isNaN(n) ? 0 : n;
};
const ts = (r) =>
  toMs(r?.startedAt) ||
  toMs(r?.startedAtMs) ||
  toMs(r?.timing?.start) ||
  toMs(r?.startedAtISO) ||
  toMs(r?.timestamp) ||
  0;

const fmtCpm  = (v) => v == null ? '—' : `$${Number(v).toFixed(2)}`;

/* ─── helpers ───────────────────────────────────────────────── */

// Prebid validData accessors — handles both storage layouts:
//   New captures: validData.tables.imps / validData.tables.bids  (nested)
//   Old captures: validData.imps / validData.bids                (flat)
function getPrebidImps(row) {
  return row?.validData?.tables?.imps || row?.validData?.imps || [];
}
function getPrebidBids(row) {
  return row?.validData?.tables?.bids || row?.validData?.bids || [];
}

// Extract a top-level query param from a URL (safe, returns '' on failure)
function qp(url, key) {
  try { return new URL(url).searchParams.get(key) || ''; } catch { return ''; }
}

// Parse GAM cust_params from the request URL directly (validData.cust is not persisted)
function parseCustParams(url) {
  try {
    const raw = new URL(url).searchParams.get('cust_params') || '';
    const decoded = (() => { try { return decodeURIComponent(decodeURIComponent(raw)); } catch { return decodeURIComponent(raw); } })();
    const out = {};
    decoded.split('&').forEach(pair => {
      const eq = pair.indexOf('=');
      if (eq < 0) return;
      const k = pair.slice(0, eq).trim();
      const v = pair.slice(eq + 1).trim();
      if (k) out[k] = v;
    });
    return out;
  } catch { return {}; }
}

/* Join keys for manually confirming a display slot in GAM Delivery Inspector
   (no API needed — paste into the console). Same keys a connector would join on. */
export function buildDisplayLookupKeys(slot) {
  const gam = slot?.phases?.gam?.row || null;
  const url = gam?.url || '';
  const vd  = gam?.validData || {};
  const cp  = parseCustParams(url);
  let correlator = vd.correlator || null;
  if (!correlator) { try { correlator = new URL(url).searchParams.get('correlator'); } catch {} }
  const cs = vd.expanded?.creativeSource || null;

  const obj = {
    adUnit:       slot?.adUnit || vd.adUnit || null,
    correlator,
    hb_pb:        cp.hb_pb || null,
    hb_bidder:    cp.hb_bidder || null,
    hb_adid:      cp.hb_adid || null,
    hb_cache_id:  cp.hb_cache_id || null,
    amzn_b:       cp.amzn_b || null,
    rendered:     cs ? `${cs.source}${cs.confirmed === false ? ' (unconfirmed)' : cs.confirmed ? ' (confirmed)' : ''}` : null,
    timestampUtc: slot?.timestamp ? new Date(slot.timestamp).toISOString() : null,
  };
  const line = (k, v) => (v ? `${(k + ':').padEnd(15)} ${v}` : '');
  const text = [
    'AdLens FX — display lookup keys',
    line('ad unit', obj.adUnit),
    line('correlator', obj.correlator),
    line('hb_pb', obj.hb_pb),
    line('hb_bidder', obj.hb_bidder),
    line('hb_adid', obj.hb_adid),
    line('hb_cache_id', obj.hb_cache_id),
    line('amzn_b', obj.amzn_b),
    line('rendered', obj.rendered),
    line('time (UTC)', obj.timestampUtc),
  ].filter(Boolean).join('\n');
  return { obj, text };
}

// Slot label: full ad unit path (e.g. /4145/fsp/iosmob/story/adh)
function slotLabel(adUnit) {
  const parts = String(adUnit || '').split('/').filter(Boolean);
  return '/' + parts.join('/') || adUnit || '?';
}

/**
 * Inspect an Amazon row's response body to determine if APS actually returned a bid.
 * Amazon returns a JSON array of slot objects — each with an `ads` array.
 * An empty `ads` array (or empty body) = no-bid. Populated `ads` = bid returned.
 *
 * Returns: 'bid' | 'nobid' | 'error' | 'unknown'
 */
function detectAmazonBidStatus(row) {
  if (!row) return null;
  const httpStatus = Number(row.status || row.responseCode || 0);
  if (httpStatus >= 400) return 'error';

  const body = row.responseBody;
  if (body == null || body === '' || (typeof body === 'string' && body.trim().length < 3)) {
    return 'nobid'; // empty response = no-bid
  }

  try {
    const parsed = typeof body === 'object' ? body : JSON.parse(body);

    // Shape 1: { "ads": {}, "errorCode": "200", "status": "ok" }
    // ads is an object — empty object = no-bid, has keys = bid
    if (parsed?.ads !== undefined) {
      if (Array.isArray(parsed.ads)) {
        return parsed.ads.length > 0 ? 'bid' : 'nobid';
      }
      if (typeof parsed.ads === 'object' && parsed.ads !== null) {
        return Object.keys(parsed.ads).length > 0 ? 'bid' : 'nobid';
      }
    }

    // Shape 2: array of slot objects e.g. [{ slotID: "...", ads: [{...}] }]
    if (Array.isArray(parsed)) {
      const hasBid = parsed.some(slot =>
        (Array.isArray(slot.ads) && slot.ads.length > 0) ||
        (slot.ads && typeof slot.ads === 'object' && Object.keys(slot.ads).length > 0)
      );
      return hasBid ? 'bid' : 'nobid';
    }

    // Shape 3: { slots: [...] }
    if (parsed?.slots) {
      const hasBid = parsed.slots.some(s => Array.isArray(s.ads) && s.ads.length > 0);
      return hasBid ? 'bid' : 'nobid';
    }

    // Has amzn_b directly in body
    if (parsed?.amzn_b || parsed?.ad) return 'bid';

    // Status field indicates no fill
    if (parsed?.status === 'ok' || parsed?.errorCode === '200') return 'nobid';

    return 'nobid'; // unknown shape but no recognisable bid signal — treat as no-bid
  } catch {
    return typeof body === 'string' && body.trim().length > 10 ? 'bid' : 'nobid';
  }
}

/**
 * Slot-size-aware APS bid status.
 *
 * APS fires ONE batch request per page load covering ALL slots simultaneously.
 * Time-only matching links that batch to every GAM slot — but if the APS response
 * only has bids for 300x250 and the GAM slot is 320x50, the bid doesn't apply here.
 *
 * APS response `ads` object has slot keys like `m300x250p105` (WxH encoded).
 * We parse those keys, check for size overlap with the GAM slot, and if there
 * is no overlap we return 'no_slot' — meaning "APS was captured but didn't bid
 * for this slot's sizes". The issue logic then treats this like nobid (silent).
 *
 * Returns: 'bid' | 'nobid' | 'no_slot' | 'error' | 'unknown' | null
 */
function detectAmazonBidStatusForSlot(amazonRow, gamSizeSet) {
  const overall = detectAmazonBidStatus(amazonRow);
  if (!amazonRow || overall == null || overall === 'error' || overall === 'unknown') return overall;
  if (overall === 'nobid') return 'nobid';
  // overall === 'bid': verify it's for this slot's sizes
  if (!gamSizeSet || gamSizeSet.size === 0) return overall;

  // ── Check 1: Request-side slot sizes (slots[].sz = "320x50") ────────────────
  // The APS request body lists exactly which sizes each slot requested. If the
  // request is captured, this is the most reliable check — no response parsing needed.
  const reqSlots = amazonRow.validData?.expanded?.slots;
  if (Array.isArray(reqSlots) && reqSlots.length > 0) {
    const hasMatchingReqSlot = reqSlots.some(s => {
      const sz = normSizeStr(s.sz || '');
      return sz && gamSizeSet.has(sz);
    });
    if (!hasMatchingReqSlot) return 'no_slot'; // APS batch didn't include this slot's sizes
    // Size matched in request — check the response to see if that size actually got a bid
  }

  // ── Check 2: Response ads object keys (m320x50p…) ───────────────────────────
  const body = amazonRow.responseBody;
  if (!body) return overall; // response not captured — trust overall

  try {
    const parsed = typeof body === 'object' ? body : JSON.parse(String(body));
    const ads = parsed?.ads;
    if (ads && typeof ads === 'object' && !Array.isArray(ads)) {
      const slotKeys = Object.keys(ads);
      if (slotKeys.length === 0) return 'nobid';
      for (const slotKey of slotKeys) {
        const m = String(slotKey).match(/(\d+)[x×](\d+)/i);
        if (!m) continue;
        const sz = normSizeStr(`${m[1]}x${m[2]}`);
        if (gamSizeSet.has(sz)) {
          const slotData = ads[slotKey];
          const hasBid = slotData?.b || slotData?.c || slotData?.amzn_b;
          return hasBid ? 'bid' : 'nobid';
        }
      }
      // Response captured but no matching size key — APS responded without this size
      return 'no_slot';
    }
    return overall;
  } catch {
    return overall;
  }
}

/* ─── size normalization (shared by assembler + BidTable) ────── */
// "320×50" | "320x50" → "320x50"
function normSizeStr(s) {
  return (s || '').trim().toLowerCase().replace(/×/g, 'x').replace(/\s+/g, '');
}
// GAM sz= string "430×67|300×50|320×50" → Set { "430x67", "300x50", "320x50" }
function parseGamSizes(str) {
  if (!str) return new Set();
  return new Set(str.split('|').map(normSizeStr).filter(Boolean));
}
// Prebid imps array (each imp.sizes = "300x250,320x50") → Set of all requested sizes
function parsePrebidImpSizes(imps) {
  const out = new Set();
  (imps || []).forEach(imp => {
    (imp.sizes || '').split(',').map(normSizeStr).filter(Boolean).forEach(s => out.add(s));
  });
  return out;
}
// Amazon request body expanded.slots → Set of sizes APS was asked to bid on.
// APS slot objects carry sizes as [[w,h], ...] or {w, h}.
function parseAmazonSlotSizes(amazonRow) {
  const slots = amazonRow?.validData?.expanded?.slots;
  if (!Array.isArray(slots)) return new Set();
  const out = new Set();
  slots.forEach(s => {
    if (Array.isArray(s.sizes)) {
      s.sizes.forEach(sz => {
        if (Array.isArray(sz) && sz.length === 2) out.add(normSizeStr(`${sz[0]}x${sz[1]}`));
        else if (typeof sz === 'string') out.add(normSizeStr(sz));
      });
    } else if (s.w && s.h) {
      out.add(normSizeStr(`${s.w}x${s.h}`));
    } else if (s.size) {
      out.add(normSizeStr(s.size));
    }
  });
  return out;
}
// Sentinel sizes used by bidders to signal passback / no-fill — never render as real creatives.
const SENTINEL_SIZES = new Set([
  '9544x9544', // The Trade Desk passback sentinel
  '1x1',       // Generic tracking pixel / passback
  '2x2',       // Variant passback sentinel
  '0x0',       // Null creative
  '9999x9999', // Generic large sentinel
]);
function isSentinelSize(sizeStr) {
  return SENTINEL_SIZES.has(normSizeStr(sizeStr));
}


/**
 * resolveGraphSource(server, source, local) — which implementation's answer the
 * Lifecycle tab renders, as a pure function.
 *
 * Extracted rather than left as inline ternaries because this IS the migration:
 * "use the server's graph, fall back to the browser's, and never mix the two".
 * Mixing is the specific hazard — taking groups from the server while
 * redundancies come from local would produce a panel whose parts disagree, and
 * inline ternaries make that a one-character mistake. Here it is one decision
 * applied to all four outputs, and it is unit-testable without rendering a
 * component (this repo has no component-rendering test setup — every test is a
 * pure-function test, and this keeps to that).
 *
 * A server response missing ANY of the four is treated as unusable and falls
 * back wholesale, so a partial/older proxy can never half-populate the panel.
 *
 * All four local inputs are THUNKS (WP1c). The local graph costs real work on a
 * large session, and when the server answers — the normal case — none of it is
 * needed. Passing values meant paying for the fallback on every render even
 * though it was discarded. The atomic contract is unchanged: either all four
 * outputs come from the server or all four come from the thunks.
 *
 * EXTENDED (WP3c) with a FIFTH output, `omidSessions`, decided by its OWN
 * atomic check rather than folded into `complete` above. It is still a strict
 * server-or-local decision for its whole output — there is just no partial
 * "half from server" state for a single array — but it deliberately does not
 * require `complete`: `omidSessions` rides on the same POST /proxy/lifecycle
 * response whenever `detail:true` was requested, independent of whether the
 * caller also asked for the full graph (`full`/`grouped`), so gating it behind
 * the four-output `complete` check would force an unrelated fallback (e.g. a
 * caller that only wants OMID would never see the server's answer). `local`'s
 * `omidSessions` thunk is OPTIONAL — callers that don't pass one (including
 * every pre-WP3c test fixture) simply get `omidSessions: undefined`, so this
 * extension cannot break a caller that doesn't know about it yet.
 */
export function resolveGraphSource(serverGraph, source, local) {
  const complete = source === 'server'
    && !!serverGraph
    && Array.isArray(serverGraph.lifecycles)
    && Array.isArray(serverGraph.groups)
    && Array.isArray(serverGraph.redundancies);

  const usingServerOmid = source === 'server' && !!serverGraph && Array.isArray(serverGraph.omidSessions);
  const omidSessions = usingServerOmid
    ? serverGraph.omidSessions
    : (local && typeof local.omidSessions === 'function' ? local.omidSessions() : undefined);

  if (!complete) {
    const lifecycles = local.lifecycles();
    return {
      usingServer: false,
      lifecycles,
      groups: local.groups(lifecycles),
      redundancies: local.redundancies(lifecycles),
      orphanReport: local.orphanReport(lifecycles),
      omidSessions,
      usingServerOmid,
    };
  }
  return {
    usingServer: true,
    lifecycles: serverGraph.lifecycles,
    groups: serverGraph.groups,
    redundancies: serverGraph.redundancies,
    orphanReport: serverGraph.orphanReport || local.orphanReport(local.lifecycles()),
    omidSessions,
    usingServerOmid,
  };
}

/* ─── mediation (SDK-bidding) impression joins ────────────────
 * A mediation win ships no creative and its impression pings often never reach the
 * proxy (QUIC), so a slot whose creativeSource is 'mediation' gets two extra tiers,
 * inserted ahead of the weak time-window guess (every other slot is untouched):
 *   impUrl       — a captured row's URL matches one of the GAM response's
 *                  ad_networks[0].imp_urls (Google adview ?ai=, Liftoff api/bill
 *                  ?settlement_price=). Deterministic.
 *   sdkTelemetry — Liftoff SDK `impression` event (validData.sdkEvent, written by
 *                  adQuality/liftoffEvent.js) inside 60s after the GAM request, size
 *                  gated. Lower confidence; refuses to guess when two slots qualify.
 * Keep in lockstep with the frontend twin (AdLifecycleView.jsx). */
const MEDIATION_TELEMETRY_WINDOW_MS = 60000;
const MEDIATION_SIBLING_WINDOW_MS   = 30000;
const MEDIATION_PLACEMENT_RE = /^(in-?line|banner|mrec)$/i;
const SOUND_ON_CODE = 'banner_autoplay_sound_on';

// { network, isLiftoff, impUrls } for a mediation-win GAM row, else null. Reads the
// compact capture-time fields; older captures fall back to the response body.
function mediationInfoOf(gamRow, expanded) {
  try {
    const cs = expanded && expanded.creativeSource;
    let isMed = !!(cs && cs.source === 'mediation');
    const noSource = !cs || !cs.source || cs.source === 'unknown';
    let network = isMed ? (cs.network || ((/—\s*(.+)$/.exec(cs.label || '') || [])[1]) || null) : null;
    let impUrls = Array.isArray(expanded && expanded.mediationImpUrls) ? expanded.mediationImpUrls : null;
    if (noSource || (isMed && (!impUrls || !network))) {
      const body = gamRow && gamRow.responseBody;
      const text = typeof body === 'string' ? body : (body ? JSON.stringify(body) : '');
      if (text.length > 10) {
        let parsed = null;
        try { parsed = JSON.parse(text); } catch { /* not JSON */ }
        if (parsed) {
          const med = mediationFillOf(parsed);
          if (!isMed && noSource && parsed.ad_type === '' && med) { isMed = true; network = med.network; }
          if (isMed && !network && med) network = med.network;
          if (isMed && !impUrls) impUrls = mediationImpUrlsOf(parsed);
        }
      }
    }
    if (!isMed) return null;
    return {
      network,
      isLiftoff: /vungle|liftoff/i.test(network || (cs && cs.label) || ''),
      impUrls: impUrls || [],
    };
  } catch { return null; }
}

// Does a captured ping URL carry the same identity as one the GAM response promised?
function sameImpPing(rowUrl, impUrl) {
  try {
    if (!rowUrl || !impUrl) return false;
    if (rowUrl === impUrl) return true;
    const a = new URL(rowUrl), b = new URL(impUrl);
    if (a.host !== b.host || a.pathname !== b.pathname) return false;
    if (/\/pagead\/adview\/?$/.test(b.pathname)) {
      const ai = b.searchParams.get('ai');
      return !!ai && ai === a.searchParams.get('ai');
    }
    if (/\/api\/bill\/?$/.test(b.pathname) && /(^|\.)vungle\.com$/i.test(b.hostname)) {
      const sp = b.searchParams.get('settlement_price');
      return !!sp && sp === a.searchParams.get('settlement_price');
    }
    return false;
  } catch { return false; }
}

// validData.sdkEvent of a Liftoff telemetry row (adQuality/liftoffEvent.js), else null.
function sdkEventOf(r) {
  const e = r && r.validData && r.validData.sdkEvent;
  return e && typeof e === 'object' && e.network === 'liftoff' ? e : null;
}

/* ─── slot lifecycle assembler ─────────────────────────────── */
// A Prebid/APS row the proxy answered itself because of a Chaos block/mock rule
// (proxyOnRequestHandler stamps row.chaos). Its synthetic 504/mock is NOT the
// bidder's answer, so the slot must say "blocked (Chaos)", not "0 bids"/"error".
// Twin of proxy-server/lifecycle/buildSlotLifecycles.js chaosOf.
function chaosOf(row) {
  const c = row && (row.chaos || (row.validData && row.validData.chaos));
  if (!c || (c.action !== 'block' && c.action !== 'mock')) return null;
  return { action: c.action, id: c.id || null, status: c.status ?? null };
}

// opts.packs: App Pack map the Meta placement check reads the imported report from (default
// getPacks()). Twin of proxy lifecycle/buildSlotLifecycles.js; the parity suite injects the same map.
export function buildSlotLifecycles(rows, manualOverrides = {}, opts = {}) {
  let _placementSession;
  let _placementPacks;
  const placementCtx = {
    get session() { if (_placementSession === undefined) _placementSession = resolveSessionMapping(rows); return _placementSession; },
    get packs() { if (_placementPacks === undefined) _placementPacks = opts && opts.packs !== undefined ? opts.packs : getPacks(); return _placementPacks; },
  };
  // `r &&` guard matches proxy-server/lifecycle/buildSlotLifecycles.js:330 — the
  // server port hardened this and the fix was never brought back here, so a
  // single null row in the array crashed the whole Lifecycle tab. Found by
  // validation/lifecycleParity.test.js.
  const byType = (t) => rows.filter(r => {
    const rt = ((r && (r.type || r.requestType)) || '').toLowerCase();
    return Array.isArray(t) ? t.includes(rt) : rt === t;
  });

  // Only include GAM requests where the IU is a real publisher ad unit (≥2 path segments).
  // Third-party creatives (e.g. Google programmatic ads) fire their own /gampad/ads calls
  // with single-segment IDs like /1779040368090-mg4p7q — these are NOT publisher slots.
  // A valid publisher IU always has: /networkId/SlotPath (e.g. /4145/FNC.mobile/ARTICLE/adh).
  const gamRows        = byType('google').filter(r => {
    const iu = r.validData?.adUnit || r.id || '';
    return iu.split('/').filter(Boolean).length >= 2;
  });
  const prebidRows     = byType('prebid');
  // Only 'amazon' (display APS SDK) — amazon_video (OTT C2S) is video-only, handled in VideoLifecycleView
  const amazonRows     = byType('amazon');
  // Impressions: OMID beacons, Google impression pings, Amazon events, bidder win notifications
  const impressionRows = byType([
    'google_impression',    // GAM gampad/adview, pcs/view
    'omid_impression',      // type=omidimpression OMID beacon
    'omid_measurement',     // /pagead/gen_204 OMID pings
    'amazon_impression',    // aax-events, x/px Amazon events
    'prebid_win',           // events.prebid.org/event?t=win
    'bidder_impression',    // per-bidder win/impression trackers
    'impression',           // generic fallback
  ]).filter(r => !isActiveViewRow(r));
  // ^ An ActiveView ping (/pcs/activeview, gen_204?id=av-js) is measurement, NOT an impression. Excluded
  //   by URL as well as type: saved HARs keep the stale google_impression type (viewability DA3).
  const clickRows      = byType([
    'google_click',         // aclk, gampad/jump
    'click',                // generic fallback
  ]);
  // WP4: mediation SDK-bidding buyer traffic — built once per call, attributed
  // per-slot by buildMediationPhase() using timing-window proximity.
  const sdkBiddingRows = byType('sdk_bidding');
  // GMA mediation fill-result reports (pagead/interaction label=drx_mediation_request_fill_result):
  // did the winning adapter load its ad? Joined per slot by sigh (mediationFillResult.js).
  const fillResultRows = rows.filter(isFillResultRow);

  // Index Prebid rows by every hb_cache_id in their bids table
  // FIX: bids live at validData.tables.bids (from inspectPrebid), not validData.bids
  const prebidByCacheId = new Map();
  prebidRows.forEach(r => {
    getPrebidBids(r).forEach(bid => {
      const cid = bid.cache;
      if (cid && cid !== '-') prebidByCacheId.set(cid, r);
    });
  });

  // ── O(1) impression lookup indexes ──────────────────────────────────────
  // Pre-built once per buildSlotLifecycles call so each lifecycle pays O(1)
  // instead of O(n) linear scan.  Keys are the unique params extracted from
  // the impression beacon URL by the proxy (validateGAMRequest.js).
  //
  //  impByXai        — xai (extended ad id, unique per slot per auction) → row
  //  impBySai        — sai (session ad id, secondary unique key) → row
  //  impByCorrelator — correlator (page-load scoped) → row
  //
  // xai is the strongest: one xai value lives in the GAM response's
  // impression_urls, and the same xai appears in the beacon the SDK fires.
  const impByXai        = new Map();
  const impBySai        = new Map();
  const impByCorrelator = new Map();
  for (const r of impressionRows) {
    const rUrl = r.url || '';
    try {
      const u = new URL(rUrl);
      const xai  = u.searchParams.get('xai');
      const sai  = u.searchParams.get('sai');
      const corr = u.searchParams.get('correlator') || r.validData?.correlator;
      if (xai  && !impByXai.has(xai))        impByXai.set(xai, r);
      if (sai  && !impBySai.has(sai))        impBySai.set(sai, r);
      if (corr && !impByCorrelator.has(corr)) impByCorrelator.set(corr, r);
    } catch { /* non-URL impression rows (OMID etc.) — skip index, fall through to tiers */ }
  }

  // ── WP3: win-beacon URL index (response→request proof) ─────────────────
  // Bid nurl/burl/lurl (surfaced on validData.tables.bids[] by WP0) are the
  // bidder's own win/billing notification URLs. If a 'bidder_impression' or
  // 'prebid_win' beacon row's URL exactly matches (or is prefixed by) one of
  // those, the win actually fired — not just "claimed by the bid table".
  // Keyed by exact URL string; a second, query-stripped key handles beacons
  // that append cache-busting params the notification URL doesn't have.
  const winBeaconByUrl = new Map();
  const stripQuery = (u) => { const i = u.indexOf('?'); return i === -1 ? u : u.slice(0, i); };
  for (const r of impressionRows) {
    const rt = (r.type || r.requestType || '').toLowerCase();
    if (rt !== 'bidder_impression' && rt !== 'prebid_win') continue;
    const rUrl = r.url || '';
    if (!rUrl) continue;
    if (!winBeaconByUrl.has(rUrl)) winBeaconByUrl.set(rUrl, r);
    const stripped = stripQuery(rUrl);
    if (!winBeaconByUrl.has(stripped)) winBeaconByUrl.set(stripped, r);
  }
  // Given a notification URL (nurl/burl/lurl), find the beacon row that fired it.
  // Exact match first, then prefix match (beacon URL starts with the notification
  // URL, or vice versa — handles trailing/leading query differences defensively).
  function findWinBeacon(notifyUrl) {
    if (!notifyUrl) return null;
    const direct = winBeaconByUrl.get(notifyUrl);
    if (direct) return direct;
    const strippedNotify = stripQuery(notifyUrl);
    const byStripped = winBeaconByUrl.get(strippedNotify);
    if (byStripped) return byStripped;
    for (const [key, row] of winBeaconByUrl) {
      if (key.startsWith(strippedNotify) || strippedNotify.startsWith(stripQuery(key))) return row;
    }
    return null;
  }

  // Track claimed rows so each impression/click is assigned to exactly ONE lifecycle.
  // Without this, every refresh of the same slot matches the same click/impression
  // because they share the same iu= adUnit and the time window is wide.
  const claimedImpression = new Set();
  const claimedClick      = new Set();

  // Mediation (SDK-bidding) joins — see the helper block above.
  const sdkEventRows = rows.filter((r) => sdkEventOf(r));
  const medInfoCache = new Map();
  const medInfoFor = (g) => {
    if (!medInfoCache.has(g)) medInfoCache.set(g, mediationInfoOf(g, (g.validData && g.validData.expanded) || {}));
    return medInfoCache.get(g);
  };
  const adUnitOfGam = (g) => (g.validData && g.validData.adUnit) || g.id || '';
  const slotSizeSet = (g) => {
    const exp = (g.validData && g.validData.expanded) || {};
    const set = new Set();
    [(exp.meta && exp.meta.sizes) || (g.validData && g.validData.sizes) || qp(g.url, 'sz'), qp(g.url, 'prev_iu_szs')]
      .forEach((str) => String(str || '').split(/[|,]/).map(normSizeStr).filter(Boolean).forEach((x) => set.add(x)));
    return set;
  };
  // Event size: its own width/height, else the nearest same-placement sibling event's.
  const telemetrySize = (r) => {
    const e = sdkEventOf(r);
    if (!e) return null;
    if (e.width && e.height) return normSizeStr(`${e.width}x${e.height}`);
    if (!e.placementRef) return null;
    const rTs = ts(r);
    let best = null, bestD = Infinity;
    for (const o of sdkEventRows) {
      const oe = sdkEventOf(o);
      if (!oe || oe.placementRef !== e.placementRef || !(oe.width && oe.height)) continue;
      const d = Math.abs(ts(o) - rTs);
      if (d <= MEDIATION_SIBLING_WINDOW_MS && d < bestD) { best = oe; bestD = d; }
    }
    return best ? normSizeStr(`${best.width}x${best.height}`) : null;
  };
  // Could telemetry impression `r` belong to mediation slot `g`?
  const telemetryQualifies = (g, r) => {
    const info = medInfoFor(g);
    const e = sdkEventOf(r);
    if (!info || !info.isLiftoff || !e || e.name !== 'impression') return false;
    if (!MEDIATION_PLACEMENT_RE.test(e.placementType || '')) return false;
    const gTs = ts(g), rTs = ts(r), diff = rTs - gTs;
    if (diff < 0 || diff > MEDIATION_TELEMETRY_WINDOW_MS) return false;
    const size = telemetrySize(r), set = slotSizeSet(g);
    if (size && set.size && !set.has(size)) return false;
    const iu = adUnitOfGam(g);
    // A later refresh of the same ad unit owns it.
    if (iu && gamRows.some((o) => o !== g && adUnitOfGam(o) === iu && ts(o) > gTs && ts(o) <= rTs)) return false;
    return true;
  };
  // Earliest unclaimed telemetry impression that qualifies for mediation slot `g`.
  const firstTelemetryFor = (g) => {
    let best = null;
    for (const r of sdkEventRows) {
      if (claimedImpression.has(r._k ?? r.id) || !telemetryQualifies(g, r)) continue;
      if (!best || ts(r) < ts(best)) best = r;
    }
    return best;
  };
  const matchedGam = new Set();   // slots that already linked an impression (never rivals)
  // A row that is the promised ping of a mediation slot is reserved for that slot: another
  // slot's heuristic tiers (ad unit / time window) must not steal it.
  const impUrlOwner = new Map();
  for (const g of gamRows) {
    const info = medInfoFor(g);
    if (!info || !info.impUrls.length) continue;
    for (const r of rows) {
      if (!r || r === g || !r.url || impUrlOwner.has(r)) continue;
      if (info.impUrls.some((u) => sameImpPing(r.url, u))) impUrlOwner.set(r, g);
    }
  }
  const impUrlRowFor = (g) => rows.find((r) => r && impUrlOwner.get(r) === g && !claimedImpression.has(r._k ?? r.id)) || null;
  // NOTE: Prebid and Amazon are NOT claimed exclusively.
  // Both are batch requests that serve ALL slots on a page in one shot —
  // one Prebid auction and one APS request cover every GAM slot that fires
  // in that page load. Claiming them after the first GAM match leaves every
  // other slot showing "—" for Prebid/APS even though they were part of the
  // same auction. Time-window proximity is enough to correctly scope them.
  // NOTE: Prebid and Amazon are NOT claimed exclusively.
  // Both are batch requests that serve ALL slots on a page in one shot —
  // one Prebid auction and one APS request cover every GAM slot that fires
  // in that page load. Claiming them after the first GAM match leaves every
  // other slot showing "—" for Prebid/APS even though they were part of the
  // same auction. Time-window proximity is enough to correctly scope them.

  // One lifecycle per GAM row (each GAM call = one slot auction)
  // Every GAM request time — a billing notice attaches to the nearest one before it.
  const gamTimestamps = gamRows.map((r) => ts(r));
  const lifecycles = gamRows.map((gamRow) => {
    const gamTs    = ts(gamRow);
    const expanded = gamRow.validData?.expanded || {};
    const cacheId  = expanded.prebidId || null;
    const adUnit   = gamRow.validData?.adUnit || gamRow.id || '';
    const sizes    = expanded.meta?.sizes || gamRow.validData?.sizes || '';

    // No-fill: GAM returned an empty response — no creative, no impression, no click.
    // Skip impression/click matching entirely to avoid time-proximity false positives
    // where a beacon from an adjacent slot gets incorrectly claimed here.
    const isNoFill = expanded.creativeSource?.source === 'no-fill';

    /* Prebid – matching strategy (primary source = GAM cust_params hb_* params).
     *
     * Tier 0 (hb_cache_id token — strongest possible):
     *   GAM cust_params carries hb_cache_id=<id>. The Prebid bid row that generated
     *   this cache entry is the exact Prebid batch for this slot. Direct 1:1 link,
     *   no timing required. Evaluated against the pre-built prebidByCacheId index.
     *
     * Tier 0b (hb_bidder + hb_pb response-body match):
     *   When hb_cache_id is absent but hb_bidder is in GAM cust_params, find the
     *   Prebid row whose tables.bids contains a bid from that bidder at that price
     *   (within ±$0.01 tolerance for float serialisation). Matches even when the
     *   Prebid cache was not captured.
     *
     * Tier 1 (gpid structural): Prebid imp ext.gpid === GAM adUnit, fired ≤8 s before.
     *   One-to-one structural link — handles multiple concurrent slot auctions.
     *
     * Tier 2 (time proximity): ≤6 s before GAM — last resort when gpid not set.
     *
     * NOT exclusively claimed: Prebid fires ONE batch request per page load. */

    // Hoist cust_params parse so both Prebid (Tier 0b) and APS (Tier 0) matching can use it.
    // validData.cust is not persisted — always parse directly from the raw GAM URL.
    const custParamsEarly = parseCustParams(gamRow.url || '');
    const gamAmznB        = custParamsEarly.amzn_b    || null;  // APS bid token
    const gamAmznSlots    = custParamsEarly.amznslots  || null;  // APS slot string

    // Manual override: user explicitly attached a Prebid row to this GAM lifecycle
    const gamOverride = manualOverrides[gamRow._k] || {};

    let prebidAmbiguous = null; // { count, gamCount, candidates[] } — set when auto-match fails
    let prebidMatchedBy = null; // 'manual' | 'cache_id' | 'bidder_price' | 'gpid' | 'timing'
    const prebidRow = (() => {
      // Manual override wins unconditionally — user knows best
      if (gamOverride.prebidRowKey) {
        const manual = prebidRows.find(r => r._k === gamOverride.prebidRowKey);
        if (manual) { prebidMatchedBy = 'manual'; return manual; }
      }

      // Tier 0: hb_cache_id in GAM cust_params — unambiguous 1:1 link.
      // Also check bidder-scoped key (hb_cache_id_thetradedesk style).
      const gamCacheId = cacheId
        || Object.keys(custParamsEarly).reduce((found, k) => {
          if (found) return found;
          if (k.startsWith('hb_cache_id_')) return custParamsEarly[k] || null;
          return null;
        }, null);
      if (gamCacheId) {
        const byCache = prebidByCacheId.get(gamCacheId);
        if (byCache) { prebidMatchedBy = 'cache_id'; return byCache; }
      }

      // Tier 0b: hb_bidder + hb_pb from GAM cust_params matched against Prebid response bids.
      // Also check bidder-scoped keys (hb_bidder_thetradedesk / hb_pb_thetradedesk).
      const gamHbBidder = custParamsEarly.hb_bidder
        || Object.keys(custParamsEarly).find(k => k.startsWith('hb_bidder_') && custParamsEarly[k])
              && custParamsEarly[Object.keys(custParamsEarly).find(k => k.startsWith('hb_bidder_'))]
        || null;
      const gamHbPb = custParamsEarly.hb_pb
        || Object.keys(custParamsEarly).find(k => k.startsWith('hb_pb_') && custParamsEarly[k])
              && custParamsEarly[Object.keys(custParamsEarly).find(k => k.startsWith('hb_pb_'))]
        || null;
      if (gamHbBidder) {
        const gamHbPbNum = gamHbPb ? parseFloat(gamHbPb) : null;
        const byBidder = prebidRows.find(r => {
          const d = gamTs - ts(r);
          if (d < 0 || d > 12000) return false; // wider 12s window for slow networks
          const bids = getPrebidBids(r);
          return bids.some(b => {
            if ((b.seat || '').toLowerCase() !== gamHbBidder.toLowerCase()) return false;
            if (gamHbPbNum == null) return true; // bidder name alone is enough
            const bidPb = parseFloat(b.hb_pb || b.price || 0);
            return Math.abs(bidPb - gamHbPbNum) < 0.05; // ±$0.05 tolerance (price bucket rounding)
          });
        });
        if (byBidder) { prebidMatchedBy = 'bidder_price'; return byBidder; }
      }

      // Tier 1: gpid match — structural link via OpenRTB imp ext.gpid === GAM adUnit.
      // Wider 12s window to handle slow/congested networks.
      // IMPORTANT: use filter+reduce (not find) so refresh cycles (same gpid, 30s apart)
      // always bind to the CLOSEST Prebid row before the GAM request, not the earliest one.
      const gpidMatches = prebidRows.filter(r => {
        const d = gamTs - ts(r);
        if (d < -1000 || d > 12000) return false; // allow 1s of clock skew
        const imps = getPrebidImps(r);
        return imps.some(imp => {
          const g = imp.gpid;
          return g && g !== '-' && g === adUnit; // use adUnit (line 575), not gamAdUnit (line 956) — TDZ
        });
      });
      if (gpidMatches.length === 1) { prebidMatchedBy = 'gpid'; return gpidMatches[0]; }
      if (gpidMatches.length > 1) {
        // Multiple rows share same gpid (refresh cycle). Pick the one fired CLOSEST before
        // this GAM request — smallest non-negative delta wins.
        const closest = gpidMatches.reduce((best, r) => {
          const d    = gamTs - ts(r);
          const bestD = gamTs - ts(best);
          // prefer rows that fired before GAM (d >= 0); among those, smallest d wins
          if (d >= 0 && (bestD < 0 || d < bestD)) return r;
          return best;
        });
        prebidMatchedBy = 'gpid';
        return closest;
      }

      // Tier 1b: stored request ID cross-reference.
      // Prebid Mobile stored requests may omit ext.gpid — use storedrequest.id instead.
      // We build a map of storedRequestId → adUnit from PREVIOUSLY matched lifecycles
      // (not available here), so instead: if exactly ONE Prebid row in window has exactly
      // ONE imp whose storedrequest ID appears in this GAM slot's cust_params `hb_adid`
      // or similar, that's our match. Simpler fallback: if exactly one row in window has
      // an imp count that equals the number of distinct GAM ad units in this page load,
      // AND the imp matching by storedRequestId to any prior match table — skip for now.
      // Practical Tier 1b: if there's only one Prebid row within window with imps that
      // all have stored IDs (i.e., it's definitely a stored-request batch), and the
      // number of imps matches the number of GAM rows on this page → claim it.
      // (This avoids guessing when slot count is consistent.)
      const storedBatches = prebidRows.filter(r => {
        const d = gamTs - ts(r);
        if (d < -1000 || d > 12000) return false;
        const imps = getPrebidImps(r);
        return imps.length > 0 && imps.every(i => i.stored);
      });
      // If exactly one stored-request batch in window AND imp count matches GAM row count
      // → this is the only candidate that could belong to this page load; claim it.
      if (storedBatches.length === 1) {
        const imps = getPrebidImps(storedBatches[0]);
        if (imps.length === gamRows.length) {
          prebidMatchedBy = 'stored_batch';
          return storedBatches[0];
        }
      }

      // Tier 2: time proximity — only confident when exactly ONE in window.
      // Multiple candidates = ambiguous; surface count to UI instead of guessing.
      const inWindow = prebidRows.filter(r => {
        const d = gamTs - ts(r);
        return d >= 0 && d < 6000;
      });
      if (inWindow.length === 1) { prebidMatchedBy = 'timing'; return inWindow[0]; }
      if (inWindow.length > 1) {
        // Record ambiguity so UI can prompt manual attachment
        prebidAmbiguous = {
          count: inWindow.length,
          gamCount: gamRows.length,
          candidates: inWindow.map(r => {
            const bids = getPrebidBids(r);
            const winner = bids.find(b => b.status === 'won');
            const bidCount = bids.filter(b => b.price > 0).length;
            const imps = getPrebidImps(r);
            // Per-imp detail lines for disambiguation (stored request ID + gpid + sizes)
            const impDetails = imps.map(i => {
              const parts = [];
              if (i.gpid && i.gpid !== '-') parts.push(i.gpid);
              if (i.stored) parts.push(`stored:${i.stored}`);
              if (i.sizes) parts.push(i.sizes);
              return parts.join(' · ');
            }).filter(Boolean);
            const label = winner
              ? `${bidCount} bid${bidCount !== 1 ? 's' : ''} · winner ${winner.seat} $${Number(winner.price || 0).toFixed(2)}`
              : bidCount > 0 ? `${bidCount} bid${bidCount !== 1 ? 's' : ''}`
              : imps.length > 0 ? `${imps.length} imp${imps.length !== 1 ? 's' : ''} — no bids` : 'Prebid request';
            return { _k: r._k, ts: ts(r), label, impDetails };
          }),
        };
      }
      return null;
    })();

    const allPrebidBids = getPrebidBids(prebidRow);
    const tmax          = prebidRow?.validData?.tables?.tmax ?? prebidRow?.validData?.tmax ?? 1000;

    // Filter bids to only those for this GAM slot's imp (gpid → imp.id → bid.impid).
    // Prebid fires ONE batch request for all slots on the page. Without filtering,
    // the /hp/adh lifecycle would show ban1+ban2 bids mixed in with adh bids.
    const prebidBids = (() => {
      const imps = getPrebidImps(prebidRow);
      // Find the imp whose gpid matches this GAM ad unit
      const slotImp = imps.find(i => i.gpid && i.gpid !== '-' && i.gpid === adUnit);
      if (!slotImp) return allPrebidBids; // gpid not set — show all bids (old behaviour)
      // Filter bids by impid; include nobid/timeout rows (which have no impid) for all seats
      const slotBids = allPrebidBids.filter(b => !b.impid || b.impid === slotImp.id);
      return slotBids.length > 0 ? slotBids : allPrebidBids; // safety fallback
    })();

    const winner   = prebidBids.find(b => b.status === 'won') || null;
    const timeouts = prebidBids.filter(b => b.status === 'timeout').length;
    const nobids   = prebidBids.filter(b => b.status === 'nobid').length;

    // ── WP3: win-beacon proof (response→request) ────────────────────────
    // `winner` (found above) already carries nurl/burl/lurl — those fields are
    // surfaced directly on each bid row by inspectPrebid.js (WP0), so no extra
    // seat/price/impid lookup is needed. Try each notification URL in priority
    // order (nurl = win notice, burl = billing notice, lurl = loss notice —
    // only relevant here if a "loss" was actually mis-marked, but we still
    // accept whichever one a beacon proves fired) against the beacon index.
    let winFired = undefined;       // undefined until we know there IS a winner
    let winMatchedBy = null;        // 'nurl' | 'burl' | 'lurl' | null
    let winBeaconRowKey = null;
    if (winner) {
      winFired = false; // winner claimed — default to "not proven" until a beacon matches
      for (const [field, url] of [['nurl', winner.nurl], ['burl', winner.burl], ['lurl', winner.lurl]]) {
        if (!url) continue;
        const beacon = findWinBeacon(url);
        if (beacon) {
          winFired = true;
          winMatchedBy = field;
          winBeaconRowKey = beacon._k ?? beacon.id ?? null;
          break;
        }
      }
    }

    // Parse GAM slot sizes early — needed for slot-aware APS matching
    const gamSizeSet = parseGamSizes(sizes);

    /* Amazon – APS → GAM matching strategy.
     *
     * APS and Prebid fire simultaneously — the SDK calls both before sending the
     * GAM request. They share the same slot set for that page load.
     * APS `slots[].slot` is an APS-specific UUID, NOT the GAM ad unit path (gpid).
     *
     * Primary source: hb_* and amzn_* params in GAM cust_params are evaluated
     * against APS/Prebid response bodies for zero-ambiguity matching before
     * falling back to any time-based heuristic.
     *
     * Tier 0 (amzn_b token match — strongest possible):
     *   GAM cust_params carries amzn_b=<token>. APS response body has
     *   ads[key].b === that token. Direct cryptographic match — no timing needed.
     *   Also accepts amznslots prefix-match as confirmation.
     *
     * Tier 1 (Prebid-anchored + size): gpid-matched Prebid timestamp ±2 s, APS
     *   slots[].sz overlaps this GAM slot's sizes. Best accuracy without token.
     *
     * Tier 2 (Prebid-anchored, no size): gpid-matched Prebid timestamp ±2 s, no
     *   size requirement. APS fires once-per-page-load alongside Prebid, so if
     *   timing matches there's no ambiguity even without size data.
     *
     * Tier 3 (size + 5 s to GAM): No Prebid anchor. Request slots[].sz matches.
     *
     * Tier 4 (response key + 5 s to GAM): Request body not captured. Parse
     *   response ads key (m320x50p…) within 5 s window.
     *
     * Tier 5 (≤2 s tight): Handles repeated ad unit refreshes (ban1 → … → ban25
     *   → ban1 again). APS for a refresh fires < 2 s before its GAM.
     *
     * Tier 6 (closest ≤5 s) / Tier 7 (closest ≤8 s): last resorts.
     *
     * NOT exclusively claimed: APS fires ONE batch per page load. */

    let amazonAmbiguous = null; // { count, gamCount, candidates[] } — set when auto-match fails
    let amazonMatchTier = null; // which tier linked APS — surfaced as match confidence
    const amazonRow = (() => {
      // Manual override wins unconditionally — user knows best
      if (gamOverride.amazonRowKey) {
        const manual = amazonRows.find(r => r._k === gamOverride.amazonRowKey);
        if (manual) { amazonMatchTier = 'manual'; return manual; }
      }

      // All APS requests within 8 s before this GAM call — outer time gate
      const candidates = amazonRows.filter(r => {
        const diff = gamTs - ts(r);
        return diff >= 0 && diff < 8000;
      });
      if (candidates.length === 0) return undefined;
      if (candidates.length === 1) { amazonMatchTier = 'single'; return candidates[0]; } // fast path — unambiguous

      // Helper: find APS with smallest time delta to a reference timestamp
      const closestTo = (ref, pool) => pool.reduce((best, r) => {
        return Math.abs(ts(r) - ref) < Math.abs(ts(best) - ref) ? r : best;
      });

      // Tier 0: amzn_b token from GAM cust_params matched against APS response body.
      // The token is the exact value APS set in ads[key].b in its response — unambiguous 1:1.
      if (gamAmznB) {
        const byToken = candidates.find(r => {
          const body = r.responseBody;
          if (!body) return false;
          try {
            const parsed = typeof body === 'object' ? body : JSON.parse(String(body));
            const ads = parsed?.ads;
            if (ads && typeof ads === 'object' && !Array.isArray(ads)) {
              return Object.values(ads).some(slotData =>
                slotData?.b === gamAmznB || slotData?.amzn_b === gamAmznB
              );
            }
            if (parsed?.amzn_b === gamAmznB) return true;
            if (gamAmznSlots && typeof parsed?.amznslots === 'string') {
              return parsed.amznslots.includes(gamAmznB);
            }
            return false;
          } catch { return false; }
        });
        if (byToken) { amazonMatchTier = 'token'; return byToken; }
      }

      // Tier 1: Prebid-anchored + size match (strongest time-based)
      if (prebidRow && gamSizeSet.size > 0) {
        const prebidTs = ts(prebidRow);
        const withSize = candidates.filter(r => {
          const delta = Math.abs(ts(r) - prebidTs);
          if (delta >= 2000) return false;
          const reqSlots = r.validData?.expanded?.slots;
          return Array.isArray(reqSlots) && reqSlots.some(s => {
            const sz = normSizeStr(s.sz || '');
            return sz && gamSizeSet.has(sz);
          });
        });
        if (withSize.length > 0) { amazonMatchTier = 'prebid_size'; return closestTo(prebidTs, withSize); }
      }

      // Tier 2: Prebid-anchored, no size requirement (APS fires once per page load)
      if (prebidRow) {
        const prebidTs = ts(prebidRow);
        const byTiming = candidates.filter(r => Math.abs(ts(r) - prebidTs) < 2000);
        if (byTiming.length > 0) { amazonMatchTier = 'prebid_timing'; return closestTo(prebidTs, byTiming); }
      }

      // Tier 3: size match within 5 s before GAM (no Prebid anchor)
      if (gamSizeSet.size > 0) {
        const tight = candidates.filter(r => gamTs - ts(r) < 5000);
        const byReqSz = tight.filter(r => {
          const reqSlots = r.validData?.expanded?.slots;
          if (!Array.isArray(reqSlots)) return false;
          return reqSlots.some(s => gamSizeSet.has(normSizeStr(s.sz || '')));
        });
        if (byReqSz.length > 0) { amazonMatchTier = 'size_req'; return closestTo(gamTs, byReqSz); }
      }

      // Tier 4: response size-key within 5 s before GAM
      if (gamSizeSet.size > 0) {
        const tight = candidates.filter(r => gamTs - ts(r) < 5000);
        const bySizeKey = tight.filter(r => {
          const body = r.responseBody;
          if (!body) return false;
          try {
            const parsed = typeof body === 'object' ? body : JSON.parse(String(body));
            const ads = parsed?.ads;
            if (!ads || typeof ads !== 'object' || Array.isArray(ads)) return false;
            return Object.keys(ads).some(slotKey => {
              const m = String(slotKey).match(/(\d+)[x×](\d+)/i);
              return m && gamSizeSet.has(normSizeStr(`${m[1]}x${m[2]}`));
            });
          } catch { return false; }
        });
        if (bySizeKey.length > 0) { amazonMatchTier = 'size_key'; return closestTo(gamTs, bySizeKey); }
      }

      // Tier 5: ≤2 s tight proximity — only confident when exactly 1 candidate.
      const tight2 = candidates.filter(r => gamTs - ts(r) <= 2000);
      if (tight2.length === 1) { amazonMatchTier = 'proximity'; return tight2[0]; }

      // Tier 6: ≤5 s, single candidate only.
      const tight5 = candidates.filter(r => gamTs - ts(r) < 5000);
      if (tight5.length === 1) { amazonMatchTier = 'proximity'; return tight5[0]; }

      // No confident match — surface ambiguity so user can attach manually.
      const pool = tight5.length > 0 ? tight5 : candidates;
      amazonAmbiguous = {
        count: pool.length,
        gamCount: gamRows.length,
        candidates: pool.map(r => {
          const reqSlots = r.validData?.expanded?.slots || [];
          const sizes = reqSlots.map(s => s.sz || '?').filter(Boolean).join(', ') || '?';
          return { _k: r._k, ts: ts(r), label: `sizes: ${sizes}` };
        }),
      };
      return null;
    })();

    // Detect what Amazon actually returned (bid / nobid / error / no_slot)
    // 'no_slot' = APS batch was captured but it didn't bid for this slot's sizes
    //  → e.g. a 300x250 APS response matched to a 320x50-only GAM slot by time proximity
    const amazonBidStatus = detectAmazonBidStatusForSlot(amazonRow, gamSizeSet); // null if no row

    // cust_params already parsed above as custParamsEarly — reuse to avoid double-parse
    const custParams      = custParamsEarly;
    const amazonPassedGAM = !!(custParams.amzn_b || custParams.amznslots);
    const amznB           = custParams.amzn_b    || null;
    const amznSlots       = custParams.amznslots || null;

    // Extract content_url for page context header
    const contentUrl = custParams.content_url || qp(gamRow.url, 'content_url') || '';

    /* Impression – 4-tier matching. Each impression row is claimed once so the
     * same beacon is never duplicated across refreshes of the same slot.
     *
     * Tier 0 — impressionUrls (cryptographic, exact):
     *   GAM response body contains the exact impression beacon URL(s) that the SDK
     *   will fire. If the proxy captures one of these URLs, it's a 1:1 match with
     *   zero ambiguity — survives many refreshes and concurrent slots.
     * Tier 1 — correlator (exact, zero-ambiguity):
     *   GAM URL has ?correlator=X. Impression pings echo same correlator.
     * Tier 2 — adUnit (iu) + 120 s window:
     *   For OMID beacons that omit correlator.
     * Tier 3 — 60 s time window only (last resort).
     */
    // GMA SDK sends correlator as 'c' param (not 'correlator').
    // validData.correlator is pre-extracted by validateGAMRequest.js from the 'c' param.
    const gamCorrelator  = gamRow.validData?.correlator || qp(gamRow.url, 'c') || qp(gamRow.url, 'correlator');
    const gamAdUnit      = adUnit;
    // Fingerprint params pre-extracted by proxy from impression_urls[0] in GAM response body.
    // xai is unique per slot per auction — the gold standard for Tier 0.
    const gamImpXai        = expanded.impXai        || null;
    const gamImpSai        = expanded.impSai        || null;
    const gamImpCorrelator = expanded.impCorrelator || null;  // from impression URL, not request URL

    // Records WHICH tier linked the impression, so the UI can show match confidence
    // (deterministic xai/sai/correlator vs a weak time-window guess) instead of a flat ✓.
    let impressionMatchTier = null;
    let mediationAmbiguous = false;   // sdkTelemetry candidate contested by another mediation slot
    const impressionRow = isNoFill ? null : (() => {
      // Tier 0a: xai match — unique per slot per auction (zero ambiguity).
      // xai is embedded in the GAM response and echoed by the SDK beacon. A GAM
      // body carries several xai tokens (impression / click / viewability), so we
      // try the primary one first then every token the proxy recovered (impXaiAll).
      const xaiCandidates = [gamImpXai, ...(expanded.impXaiAll || [])].filter(Boolean);
      for (const xai of xaiCandidates) {
        const r = impByXai.get(xai);
        if (r && !claimedImpression.has(r._k ?? r.id)) { impressionMatchTier = 'xai'; return r; }
      }

      // Tier 0b: sai match — secondary unique key, similar strength to xai.
      // On iOS the GAM request has no correlator/xai, so sai recovered from the
      // response body is the primary deterministic join. Try all recovered sai.
      const saiCandidates = [gamImpSai, ...(expanded.impSaiAll || [])].filter(Boolean);
      for (const sai of saiCandidates) {
        const r = impBySai.get(sai);
        if (r && !claimedImpression.has(r._k ?? r.id)) { impressionMatchTier = 'sai'; return r; }
      }

      // Tier 0c: correlator from impression_urls — more authoritative than request URL correlator
      // because impression_urls is what the SDK actually embeds in the beacon.
      if (gamImpCorrelator) {
        const r = impByCorrelator.get(gamImpCorrelator);
        if (r && !claimedImpression.has(r._k ?? r.id)) { impressionMatchTier = 'impCorrelator'; return r; }
      }

      // Tier 1: correlator from GAM request URL — fallback when no response body captured
      // (proxy restart, old session, or QUIC-intercepted request without response).
      // NOTE: GMA SDK uses ?c= not ?correlator= — validData.correlator is pre-extracted by proxy.
      if (gamCorrelator) {
        const r = impByCorrelator.get(gamCorrelator);
        if (r && !claimedImpression.has(r._k ?? r.id)) { impressionMatchTier = 'correlator'; return r; }
      }

      // Tier impUrl (mediation wins only): a captured ping whose URL is one the GAM response
      // promised (ad_networks[0].imp_urls — Google adview ?ai=, Liftoff api/bill ?settlement_price=).
      const impUrlHit = impUrlRowFor(gamRow);
      if (impUrlHit) { impressionMatchTier = 'impUrl'; return impUrlHit; }

      // Guard for the heuristic proximity tiers below: when the same iu is called
      // multiple times (refresh), a beacon must NOT bind to an earlier GAM if a
      // LATER GAM for the same ad unit fired between this GAM and the beacon — that
      // later refresh should own it. Without this, the Init GAM (processed first)
      // steals a refresh's impression and the render-delay reads far too high.
      // Mirrors the click matcher's hasLaterSameSlotGam guard.
      const laterSameSlotGam = (beaconTs) => gamAdUnit && gamRows.some(other => {
        if (other === gamRow) return false;
        const otherAdUnit = other.validData?.adUnit || other.id || '';
        if (otherAdUnit !== gamAdUnit) return false;
        const oTs = ts(other);
        return oTs > gamTs && oTs <= beaconTs;
      });

      // Tier 2: same adUnit + ≤30 s after this GAM request (was 120 s — too wide for rapid refreshes).
      // Handles OMID beacons that don't carry xai/correlator.
      const byAdUnit = gamAdUnit && impressionRows.find(r => {
        if (claimedImpression.has(r._k ?? r.id)) return false;
        if (impUrlOwner.has(r) && impUrlOwner.get(r) !== gamRow) return false;   // reserved for its mediation slot
        const iu = r.validData?.adSlot || qp(r.url, 'iu');
        const rTs = ts(r);
        const diff = rTs - gamTs;
        return iu && iu === gamAdUnit && diff >= 0 && diff < 30000 && !laterSameSlotGam(rTs);
      });
      if (byAdUnit) { impressionMatchTier = 'adUnit'; return byAdUnit; }

      // Tier sdkTelemetry (Liftoff mediation wins only): the SDK's own `impression` event, within
      // 60 s after the request and size-gated. Refuses to guess when another slot qualifies too.
      const telemetryPick = firstTelemetryFor(gamRow);
      if (telemetryPick) {
        // Another unmatched mediation slot whose own best candidate is this same event → don't guess.
        const rival = gamRows.some(o => o !== gamRow && !matchedGam.has(o)
          && firstTelemetryFor(o) === telemetryPick && !impUrlRowFor(o));
        if (rival) { mediationAmbiguous = true; return null; }
        impressionMatchTier = 'sdkTelemetry';
        return telemetryPick;
      }

      // Tier 3: nearest unclaimed within 60 s (last resort — no structural signal).
      const byTime = impressionRows.find(r => {
        if (claimedImpression.has(r._k ?? r.id)) return false;
        if (impUrlOwner.has(r) && impUrlOwner.get(r) !== gamRow) return false;   // reserved for its mediation slot
        const rTs = ts(r);
        const diff = rTs - gamTs;
        return diff >= 0 && diff < 60000 && !laterSameSlotGam(rTs);
      });
      if (byTime) { impressionMatchTier = 'time'; return byTime; }
      return null;
    })() || null;
    if (impressionRow) { claimedImpression.add(impressionRow._k ?? impressionRow.id); matchedGam.add(gamRow); }
    else impressionMatchTier = null;

    const impressionLatency = impressionRow ? ts(impressionRow) - gamTs : null;

    // Sound-on (Liftoff telemetry): the matched placement's own video-start row carries the
    // banner_autoplay_sound_on finding — surface it on the slot, not just on the telemetry row.
    let soundOnRow = null, soundOnIssue = null;
    if (impressionRow && impressionMatchTier === 'sdkTelemetry') {
      const impEv = sdkEventOf(impressionRow), impTs = ts(impressionRow);
      if (impEv && impEv.placementRef) {
        soundOnRow = sdkEventRows.find(r => {
          const e = sdkEventOf(r);
          if (!e || e.placementRef !== impEv.placementRef || Math.abs(ts(r) - impTs) > MEDIATION_SIBLING_WINDOW_MS) return false;
          soundOnIssue = (r.issues || []).find(i => i && i.code === SOUND_ON_CODE) || null;
          return !!soundOnIssue;
        }) || null;
      }
    }

    /* Click – 4-tier matching, claimed once per lifecycle.
     *
     * Tier 0 — sigh (cryptographic, exact):
     *   The GAM response body embeds the click URL with a unique sigh= signature.
     *   The proxy extracts it from the response and stores it as validData.clickSigh.
     *   When the user taps, the fired aclk/gampad/jump URL carries the same sigh.
     *   This is a 1:1 link — no time window, no ambiguity, survives many refreshes.
     *
     * Tier 1 — correlator (exact, no sigh):
     * Tier 2 — adUnit + time window:
     * Tier 3 — impression proximity:
     */
    const gamClickSigh = gamRow.validData?.clickSigh;

    const clickRow = isNoFill ? null : clickRows.find(r => {
      if (claimedClick.has(r._k ?? r.id)) return false;
      // Tier 0: sigh — exact cryptographic match, no time constraint needed
      if (gamClickSigh) {
        const cs = r.validData?.sigh || qp(r.url, 'sigh');
        if (cs && cs === gamClickSigh) return true;
      }
      const rTs  = ts(r);
      const diff = rTs - gamTs;
      if (diff < 0 || diff > 300000) return false;
      // Tier 1: correlator — exact, no ambiguity (GMA uses 'c' param, not 'correlator')
      if (gamCorrelator) {
        const cc = r.validData?.correlator || qp(r.url, 'c') || qp(r.url, 'correlator');
        if (cc && cc === gamCorrelator) return true;
      }
      // Tier 2 & 3 guard: only the LATEST GAM for this slot before the click should
      // claim it via proximity. If a more-recent GAM (same adUnit) sits between this
      // gamTs and the click, defer — let that visit claim the click instead.
      // Without this, Init (processed first) steals clicks that belong to R1.
      const hasLaterSameSlotGam = gamAdUnit && gamRows.some(other => {
        if (other === gamRow) return false;
        const otherAdUnit = other.validData?.adUnit || other.id || '';
        if (otherAdUnit !== gamAdUnit) return false;
        const oTs = ts(other);
        return oTs > gamTs && oTs < rTs;   // between this GAM and the click
      });
      if (hasLaterSameSlotGam) return false;
      // Tier 2: adUnit match
      if (gamAdUnit) {
        const iu = r.validData?.adSlot || qp(r.url, 'iu');
        if (iu && iu === gamAdUnit) return true;
      }
      // Tier 3: after impression within 5 min
      if (impressionRow) {
        const fromImp = rTs - ts(impressionRow);
        return fromImp >= 0 && fromImp < 300000;
      }
      return false;
    }) || null;
    if (clickRow) claimedClick.add(clickRow._k ?? clickRow.id);

    /* Issues summary */
    const issues = [];
    // Contract A (docs/slot-story-spec.md): every issue carries phase + stable code +
    // the _k ?? id of the row its evidence came from. Keep in lockstep with the twin builder.
    const _rk = r => (r ? (r._k ?? r.id ?? null) : null);
    const prebidKey = _rk(prebidRow), amazonKey = _rk(amazonRow), gamKey = _rk(gamRow);

    // ── Prebid winner sentinel size — passback, not a real fill ─────────────
    // Sizes like 9544×9544 (TTD), 1×1, 0×0 are passback sentinels.
    // Bidder technically "won" but submitted no real creative — slot will be blank.
    // A rendered impression outranks the size heuristic: 1×1 is also a legitimate
    // size-agnostic creative size, so with an impression it is a warn, never "will not render".
    if (winner?.size && isSentinelSize(winner.size)) {
      issues.push(impressionRow ? {
        level: 'warn',
        msg: `${winner.seat} bid with size ${winner.size} (usually a passback size), but the slot rendered — impression fired. Confirm which creative served in the GAM response.`,
        phase: 'prebid', code: 'prebid_sentinel_size', rowKey: prebidKey,
      } : {
        level: 'fail',
        msg: `${winner.seat} won with sentinel size ${winner.size} — this is a passback, not a real fill. Slot will not render.`,
        phase: 'prebid', code: 'prebid_sentinel_size', rowKey: prebidKey,
      });
    }

    // ── Prebid winner size vs GAM slot size cross-check ──────────────────────
    // The Prebid winning bid's creative size must be in the GAM slot's accepted sizes.
    // If not, GAM will discard the Prebid creative and serve its own demand instead.
    // (Skip if already flagged as sentinel — redundant message)
    if (winner?.size && sizes && !isSentinelSize(winner.size)) {
      const gamSizeSet = parseGamSizes(sizes);
      const winnerSizeNorm = normSizeStr(winner.size);
      if (winnerSizeNorm && gamSizeSet.size > 0 && !gamSizeSet.has(winnerSizeNorm)) {
        issues.push({
          level: 'warn',
          msg: `Size mismatch — ${winner.seat} won with ${winner.size} but slot accepts ${sizes}. GAM may serve its own demand.`,
          phase: 'prebid', code: 'prebid_winner_size_mismatch', rowKey: prebidKey,
        });
      }
    }

    // ── Prebid requested sizes not in GAM slot ────────────────────────────────
    if (prebidBids.length > 0 && sizes) {
      const gamSizeSet     = parseGamSizes(sizes);
      const prebidImpSizes = parsePrebidImpSizes(getPrebidImps(prebidRow));
      const extraSizes     = [...prebidImpSizes].filter(s => !gamSizeSet.has(s));
      if (extraSizes.length > 0) {
        issues.push({
          level: 'warn',
          msg: `Prebid requested ${extraSizes.join(', ')} — not in GAM slot sizes (${sizes}). Bids at these sizes cannot win this placement.`,
          phase: 'prebid', code: 'prebid_size_not_in_slot', rowKey: prebidKey,
        });
      }
    }

    // ── Amazon slot sizes vs GAM slot sizes ──────────────────────────────────
    // APS SDK requests bid for specific sizes. If those sizes don't match the GAM slot,
    // APS bid cannot win this placement even if Amazon returned a bid.
    if (amazonRow && sizes) {
      const gamSizeSet  = parseGamSizes(sizes);
      const amzSizes    = parseAmazonSlotSizes(amazonRow);
      if (amzSizes.size > 0) {
        const extraSizes = [...amzSizes].filter(s => !gamSizeSet.has(s));
        const overlap    = [...amzSizes].filter(s => gamSizeSet.has(s));
        if (overlap.length === 0 && extraSizes.length > 0) {
          issues.push({
            level: 'warn',
            msg: `Amazon requested ${[...amzSizes].join(', ')} — none match GAM slot sizes (${sizes}). APS bid cannot win this placement.`,
            phase: 'amazon', code: 'aps_size_mismatch', rowKey: amazonKey,
          });
        } else if (extraSizes.length > 0) {
          issues.push({
            level: 'warn',
            msg: `Amazon requested ${extraSizes.join(', ')} outside slot sizes (${sizes}) — bids at those sizes cannot win here.`,
            phase: 'amazon', code: 'aps_size_partial', rowKey: amazonKey,
          });
        }
      }
    }

    // Price bucket discrepancy
    const prebidCpm = winner ? winner.price : null;
    const gamHbPb   = expanded.hbPb ? parseFloat(expanded.hbPb) : null;
    if (prebidCpm != null && gamHbPb != null && Math.abs(prebidCpm - gamHbPb) > 0.01) {
      issues.push({
        level: 'warn',
        msg: `hb_pb mismatch — Prebid CPM ${fmtCpm(prebidCpm)} → GAM bucket ${fmtCpm(gamHbPb)} (price granularity loss)`,
        phase: 'prebid', code: 'hb_pb_mismatch', rowKey: prebidKey,
      });
    }

    // Amazon bid status → issue severity depends on what APS actually returned
    if (amazonPassedGAM) {
      // Bid confirmed in GAM cust_params — outcome is correct regardless of whether
      // we captured the APS network request (SDK may have used a cached bid response).
      // No issue to push.
    } else if (amazonRow) {
      // We have an APS request but the bid didn't reach GAM
      const amazonChaos = chaosOf(amazonRow);
      if (amazonChaos) {
        // Blocked on purpose by a Chaos rule — expected, not an APS failure.
        issues.push({ level: 'info', msg: `APS request ${amazonChaos.action === 'mock' ? 'mocked' : 'blocked'} by Chaos rule ${amazonChaos.id || ''} — no bid passed to GAM (expected)`.replace('  ', ' '), phase: 'amazon', code: 'aps_chaos', rowKey: amazonKey });
      } else if (amazonBidStatus === 'error') {
        const code = amazonRow.status || amazonRow.responseCode || '?';
        issues.push({ level: 'warn', msg: `Amazon request failed (HTTP ${code}) — no bid passed to GAM`, phase: 'amazon', code: 'aps_http_error', rowKey: amazonKey });
      } else if (amazonBidStatus === 'no_slot') {
        // APS batch was captured but it contained no slot matching this GAM slot's sizes.
        // This is normal — the captured APS request belongs to a different slot on the page.
        // Do NOT flag as a failure.
      } else if (amazonBidStatus === 'bid') {
        // APS returned a bid for this slot's sizes but it didn't reach GAM cust_params — real integration failure
        issues.push({ level: 'fail', msg: 'Amazon bid received but amzn_b missing from GAM cust_params — bid lost', phase: 'amazon', code: 'amzn_b_missing', rowKey: amazonKey });
      }
      // nobid or unknown — suppress, APS no-bid is normal
    } else {
      // No APS request AND no bid in GAM.
      // Only warn when Prebid also fired — that confirms this is a header-bidding slot where
      // APS should have participated. If Prebid is also absent this is likely a direct/house/
      // AdMob slot where neither HB partner is expected.
      if (prebidRow) {
        issues.push({ level: 'warn', msg: 'APS not captured — request may have used a URL pattern not yet recognised by proxy', phase: 'amazon', code: 'aps_not_captured', rowKey: null });
      }
    }

    // Timeouts
    if (timeouts > 0) {
      issues.push({
        level: 'warn',
        msg: `${timeouts} bidder${timeouts > 1 ? 's' : ''} timed out (tmax=${tmax}ms) — competition reduced`,
        phase: 'prebid', code: 'prebid_bidder_timeout', rowKey: prebidKey,
      });
    }

    // Malformed creative: winning bid size doesn't match any requested slot size
    if (winner && winner.size) {
      const winnerSize = String(winner.size).toLowerCase().replace(/\s/g, '');
      const is1x1 = winnerSize === '1x1';

      // Parse slot sizes from GAM sz param (e.g. "320x50|300x250")
      const slotSizes = String(sizes || '').toLowerCase().split('|').map(s => s.trim()).filter(Boolean);
      const sizeMismatch = slotSizes.length > 0 && !slotSizes.includes(winnerSize);

      if (is1x1) {
        issues.push({
          level: 'fail',
          msg: `Malformed creative — ${winner.seat} won with 1×1 pixel (tracking beacon, not a real ad). Slot expected: ${sizes || '?'}`,
          phase: 'prebid', code: 'prebid_malformed_1x1', rowKey: prebidKey,
        });
      } else if (sizeMismatch) {
        issues.push({
          level: 'warn',
          msg: `Size mismatch — ${winner.seat} won with ${winner.size} but slot requested ${sizes}. Creative may render incorrectly.`,
          phase: 'prebid', code: 'prebid_creative_size_mismatch', rowKey: prebidKey,
        });
      }
    }

    // No-fill: surface as a proper issue, suppress the generic "no impression" noise
    if (isNoFill) {
      issues.push({ level: 'warn', msg: 'GAM returned no fill — slot was blank, no creative served, impression/click matching suppressed', phase: 'gam', code: 'gam_no_fill', rowKey: gamKey });
    } else if (!impressionRow) {
      if (mediationAmbiguous) {
        issues.push({ level: 'info', msg: 'Liftoff SDK telemetry shows an impression that more than one mediation slot could own (same size window) — not guessing; no impression linked', phase: 'impression', code: 'mediation_impression_ambiguous', rowKey: gamKey });
      } else {
        issues.push({ level: 'info', msg: 'No impression beacon observed after GAM request', phase: 'impression', code: 'no_impression', rowKey: gamKey });
      }
    }
    if (soundOnRow && soundOnIssue) {
      issues.push({ level: 'fail', msg: soundOnIssue.message || soundOnIssue.note || 'Banner ad autoplayed video with sound on', phase: 'impression', code: SOUND_ON_CODE, rowKey: _rk(soundOnRow) });
    }

    // Inherit GAM validation issues (fail/warn only) plus the USB-capture info signals
    // (ingest_request_only / ingest_url_truncated) so the story can tell a partial USB row
    // from a clean one. The info ones never move overallStatus.
    const _usbInfo = i => i.code === 'ingest_request_only' || i.code === 'ingest_url_truncated';
    // The 3-cap applies to real fail/warn findings only: the USB notes ride
    // outside it, or a USB row's two notes would push out a real failure.
    const _isFailWarn = i => i.level === 'fail' || i.severity === 'fail' || i.level === 'warn' || i.severity === 'warn';
    const _gamIssues = gamRow.issues || [];
    [..._gamIssues.filter(_isFailWarn).slice(0, 3), ..._gamIssues.filter(i => !_isFailWarn(i) && _usbInfo(i))]
      .forEach(i => issues.push({
        level: i.level || i.severity,
        msg: i.note || i.message || i.msg || '',
        phase: 'gam',
        code: i.code || 'gam_row_issue',
        rowKey: gamKey,
      }));

    // A mediation winner whose SDK reported a failed load: the slot is empty even though
    // GAM's response names a buyer (Meta test bid vs live SDK request -> 1001 no fill).
    const fillResult = isNoFill ? null : fillResultForGam(gamRow, fillResultRows);
    const _fillIssue = fillResultIssue(fillResult, gamRow.validData?.expanded?.adapterLabel || null,
      { noFallback: gamRow.validData?.expanded?.adNetworkCount === 1 });
    if (_fillIssue) issues.push(_fillIssue);
    // Meta won a test auction but its SDK asked for the ad without TEST_MODE=1 (joined on BID_ID).
    const _metaTestIssue = isNoFill ? null : metaTestModeIssue(gamRow, sdkBiddingRows);
    if (_metaTestIssue) issues.push(_metaTestIssue);
    // Meta win whose placement chain breaks (same no-fill gate as the test-mode check; proxy twin wires it too).
    if (!isNoFill) issues.push(...metaPlacementIssues(gamRow, sdkBiddingRows, placementCtx));

    // USB guard (validation/usbGuard.js, twin of proxy-server/ingest/usbGuard.js): on a
    // request-only logcat GAM row, findings whose evidence is a response we cannot see
    // become info `usb_not_visible`. Slot findings about the URL itself are untouched.
    if (gamRow.captureSource === 'logcat') {
      const _truncated = _gamIssues.some(i => i && i.code === 'ingest_url_truncated');
      const _apsAbsent = !(amazonRow && amazonRow.captureSource !== 'logcat' && amazonRow.responseBody);
      issues.splice(0, issues.length, ...guardIssues(issues, { truncated: _truncated, apsResponseAbsent: _apsAbsent }));
    }

    const overallStatus = issues.some(i => i.level === 'fail') ? 'fail'
      : issues.some(i => i.level === 'warn') ? 'warn'
      : 'pass';

    // Carry the visit/page context from the GAM row so the lifecycle view
    // can be scoped to a specific page visit (set by InspectraNetworkView grouping)
    const visitKey  = gamRow._visitKey  ?? gamRow.visitKey  ?? null; // e.g. "foxnews.com/article#v1"
    const pageUrl   = gamRow._pageUrl   ?? gamRow.pageUrl   ?? null;
    const visitIdx  = gamRow._visitIdx  ?? gamRow.visitIdx  ?? null; // numeric visit index
    const visitReason = gamRow._visitReason ?? gamRow.visitReason ?? null; // 'navigation'|'returned'|'gap'

    // ── Confidence tiers + correlation-graph edges (WP2) ──────────────────
    // Additive layer over the existing match results. `tierFor` folds each
    // phase's existing matchedBy/matchTier into proven|inferred|guessed (spec
    // §3); `edges[]` is the flat, render-agnostic graph (spec §2c). Nothing
    // here changes how phases are matched — it only labels/links them.
    const clickMatchTier = clickRow
      ? (gamClickSigh && (clickRow.validData?.sigh || qp(clickRow.url, 'sigh')) === gamClickSigh
          ? 'sigh' : gamCorrelator && (clickRow.validData?.correlator || qp(clickRow.url, 'correlator')) === gamCorrelator
          ? 'correlator' : gamAdUnit && (clickRow.validData?.adSlot || qp(clickRow.url, 'iu')) === gamAdUnit
          ? 'adUnit' : 'time')
      : null;
    const prebidTier     = prebidRow ? tierFor(prebidMatchedBy) : null;
    const amazonTier     = amazonMatchTier ? tierFor(amazonMatchTier) : (amazonPassedGAM ? 'proven' : null);
    const impressionTier = impressionRow ? tierFor(impressionMatchTier) : null;
    const clickTier      = clickRow ? tierFor(clickMatchTier) : null;

    const gamNodeId = `gam:${gamRow._k ?? gamRow.id}`;
    const edges = [];
    if (prebidRow)     edges.push({ from: `prebid:${prebidRow._k ?? prebidRow.id}`,     to: gamNodeId, tier: prebidTier,     via: prebidMatchedBy || 'timing' });
    if (amazonRow || amazonPassedGAM) edges.push({ from: `amazon:${amazonRow?._k ?? amazonRow?.id ?? 'cached'}`, to: gamNodeId, tier: amazonTier, via: amazonMatchTier || (amazonPassedGAM ? 'amzn_b' : 'single') });
    if (impressionRow) edges.push({ from: gamNodeId, to: `impression:${impressionRow._k ?? impressionRow.id}`, tier: impressionTier, via: impressionMatchTier || 'time' });
    if (clickRow)      edges.push({ from: gamNodeId, to: `click:${clickRow._k ?? clickRow.id}`,                  tier: clickTier,      via: clickMatchTier || 'time' });
    // WP3: win-beacon proof edge — only when a beacon actually matched a notification URL.
    if (winFired && winBeaconRowKey) {
      edges.push({
        from: `prebid:${prebidRow?._k ?? prebidRow?.id}`,
        to: `winbeacon:${winBeaconRowKey}`,
        tier: 'proven',
        via: winMatchedBy,
      });
    }

    // ── WP4: mediation participation & outcome (spec §2b) ─────────────────
    // Participation + outcome view only (NOT a bid-by-bid waterfall — the
    // real mediation bid is server-side and never on the wire). Always
    // 'inferred' confidence; deliberately excluded from overallStatus/issues
    // above (a3p per-buyer matcher is unverified — informational only).
    const mediationPhase = buildMediationPhase({ gamRow, gamTs, sdkBiddingRows, gamTimestamps });
    mediationPhase.fillResult = fillResult || null;
    mediationPhase.buyers.forEach((b) => {
      edges.push({
        from: gamNodeId,
        to: `mediation:${b.id}`,
        tier: 'inferred',
        via: b.matchedBy,
      });
    });

    return {
      key: gamRow._k ?? gamRow.id ?? gamTs,
      edges,   // WP2: flat correlation-graph edges { from, to, tier, via }
      adUnit,
      slotLabel: slotLabel(adUnit),
      sizes,
      timestamp: gamTs,
      isRefresh: !!expanded.isRefresh,
      visitKey,
      pageUrl,
      contentUrl,   // content_url from GAM params — used for page context header
      custParams,   // parsed cust_params — used for configurable context keys (pageName, league…)
      correlator: gamCorrelator || null, // GPT pageview id — same correlator + same iu = provable duplicate
      seqNum: parseInt(qp(gamRow.url, 'seq_num') || custParams.seq_num || '', 10) || null, // GMA session counter
      visitIdx,
      visitReason,
      overallStatus,
      issues,
      phases: {
        prebid: {
          fired: !!prebidRow,
          bids: prebidBids,
          winner,
          timeouts,
          nobids,
          tmax,
          rowKey: prebidRow?._k,
          row: prebidRow || null,
          chaos: chaosOf(prebidRow),     // { action, id, status } when a Chaos rule answered it
          ambiguous: prebidAmbiguous,   // { count, gamCount, candidates[] } or null
          matchedBy: prebidMatchedBy,  // 'manual'|'cache_id'|'bidder_price'|'gpid'|'timing'|null
          tier: prebidTier,            // WP2: proven|inferred|guessed (from matchedBy)
          // WP3: response→request win proof. Only set when a winner exists;
          // undefined otherwise (older captures without nurl simply never set these).
          winFired,                    // true|false|undefined — did a beacon actually fire the winner's notify URL?
          winMatchedBy,                // 'nurl'|'burl'|'lurl'|null — which notification URL matched
          winBeaconRowKey,             // row._k of the matched bidder_impression/prebid_win beacon, or null
          manuallyAttached: !!(gamOverride.prebidRowKey && prebidRow),
          // Structural identifiers from the OpenRTB request
          // gpid = GAM ad unit path (ext.gpid); storedRequestId = Prebid server stored request ID
          // imp.sizes = banner.format sizes e.g. "300x250,320x50"
          gpid: (() => {
            const imps = getPrebidImps(prebidRow);
            const imp = imps.find(i => i.gpid && i.gpid !== '-' && i.gpid === gamAdUnit) || imps[0];
            return imp?.gpid && imp.gpid !== '-' ? imp.gpid : null;
          })(),
          storedRequestId: (() => {
            const imps = getPrebidImps(prebidRow);
            const imp = imps.find(i => i.gpid && i.gpid !== '-' && i.gpid === gamAdUnit) || imps[0];
            return imp?.stored || null;   // proxy field name is 'stored' from parsePrebid.js
          })(),
          impSizes: (() => {
            const imps = getPrebidImps(prebidRow);
            const imp = imps.find(i => i.gpid && i.gpid !== '-' && i.gpid === gamAdUnit) || imps[0];
            return imp?.sizes || null;    // "300x250,320x50" — banner.format sizes
          })(),
        },
        amazon: {
          // fired=true when we have a live captured APS request, OR when the bid
          // is confirmed in GAM cust_params (meaning APS SDK used a cached bid).
          fired: !!amazonRow || amazonPassedGAM,
          cached: !amazonRow && amazonPassedGAM, // SDK returned from cache — no new request
          passedToGAM: amazonPassedGAM,
          bidStatus: amazonBidStatus,  // 'bid' | 'nobid' | 'no_slot' | 'error' | null
          amznB,
          amznSlots,
          rowKey: amazonRow?._k,
          row: amazonRow || null,
          chaos: chaosOf(amazonRow),     // { action, id, status } when a Chaos rule answered it
          matchTier: amazonMatchTier,   // 'manual'|'token'|'prebid_size'|'prebid_timing'|'size_req'|'size_key'|'single'|'proximity'|null
          tier: amazonTier,             // WP2: proven|inferred|guessed
          ambiguous: amazonAmbiguous,   // { count, gamCount, candidates[] } or null
          manuallyAttached: !!(gamOverride.amazonRowKey && amazonRow),
          // APS slot identifiers from request body — slots[].slot (UUID), slots[].sz (size)
          apsSlots: (() => {
            const reqSlots = amazonRow?.validData?.expanded?.slots;
            if (!Array.isArray(reqSlots)) return null;
            // Only return slots whose sz matches this GAM slot's sizes (the ones that apply here)
            const matching = reqSlots.filter(s => {
              const sz = normSizeStr(s.sz || '');
              return gamSizeSet.size === 0 || gamSizeSet.has(sz);
            });
            return (matching.length > 0 ? matching : reqSlots).map(s => ({
              uuid: s.slot || null,
              sz: s.sz || null,
              slotId: s.slotId ?? null,
            }));
          })(),
        },
        gam: {
          fired: true,
          adUnit,            // full IU path e.g. /4145/FNC/IOSMOB/ART/US/ADH/ent/adh
          hbBidder: expanded.hbBidder,
          hbPb: expanded.hbPb,
          cacheId,
          isRefresh: expanded.isRefresh,
          rowKey: gamRow._k,
          row: gamRow,
          // Auction outcome from GAM response body
          winnerCpm:      expanded.winnerCpm      ?? null,
          winnerCpmGross: expanded.winnerCpmGross ?? null,
          floorCpm:       expanded.floorCpm       ?? null,
          adapter:        expanded.adapter        ?? null,
          adapterLabel:   expanded.adapterLabel   ?? null,
          allocationId:   expanded.allocationId   ?? null,
          // GAM response metadata
          impressionUrls: expanded.impressionUrls ?? null,
          adMobPubId:     expanded.adMobPubId     ?? null,
          responseType:   expanded.responseType   ?? null,
          gamSlotSizes:   expanded.gamSlotSizes   ?? null,
        },
        impression: {
          fired: !!impressionRow,
          latencyMs: impressionLatency,
          beaconType: impressionRow ? (impressionRow.type || impressionRow.requestType || 'impression') : null,
          rowKey: impressionRow?._k,
          row: impressionRow || null,
          matchTier: impressionMatchTier,   // 'xai'|'sai'|'impCorrelator'|'correlator'|'impUrl'|'adUnit'|'sdkTelemetry'|'time'|null
          matchLabel: impressionMatchTier === 'sdkTelemetry' ? 'Liftoff SDK telemetry'
            : impressionMatchTier === 'impUrl' ? 'Mediation impression ping' : null,
          tier: impressionTier,             // WP2: proven|inferred|guessed
        },
        click: {
          fired: !!clickRow,
          rowKey: clickRow?._k,
          row: clickRow || null,
          // Tier used for matching — shown in Summary panel so QA can verify
          matchTier: clickMatchTier,        // 'sigh'|'correlator'|'adUnit'|'time'|null (computed above)
          tier: clickTier,                  // WP2: proven|inferred|guessed
        },
        // WP4: mediation participation + outcome (spec §2b). Informational
        // only — never feeds overallStatus/issues (see note above edges.push).
        mediation: mediationPhase,
      },
    };
  });

  // Slot Story WP1: attach the OMID sessions that measured each slot (evidence only -
  // overallStatus / issues above are final and never see them). Twin of
  // proxy-server/lifecycle/buildSlotLifecycles.js; the join is validation/omidJoin.js.
  attachOmidSessions(lifecycles, rows);
  // Viewability WP1: attach the Google ActiveView pings that measured each slot (evidence only; the
  // slot-level issue + overallStatus recompute are WP2). Twin of proxy-server/lifecycle/buildSlotLifecycles.js.
  return attachActiveView(lifecycles, rows);
}

/* ─── redundancy thresholds (single source of truth) ───────── */
// Two GAM calls for the same slot closer than this = a duplicate (double
// loadAd()/refresh()), not a real refresh → error.
export const DUP_GAP_MS = 800;
// Google policy minimum between refreshes of the same slot. Anything faster
// (but not a sub-800ms duplicate) is a redundant early reload, NOT a refresh → warning.
export const MIN_REFRESH_MS = 30_000;

/* ─── context-aware redundancy classifier ───────────────────── */
// Given the chronologically-sorted GAM lifecycles for ONE (adUnit + visit +
// context) bucket, classify the repeat pattern and flag floods. Pure function
// of slot timestamps + the unit's role/policy — needs NO placement id.
//
//   distinct iu  → already separated upstream (the iu IS the placement id)
//   feed unit, steady run     → infinite-scroll recycled unit, legitimate
//   feed unit, ultra-tight run→ runaway loop (flood)
//   anchor unit, > N in window→ flood (a header slot can't legitimately do that)
//   otherwise per-gap:  <800ms duplicate · <30s early reload (NOT a refresh) · ≥30s refresh
function classifyContextSlots(slots, iu) {
  const policy = redundancyPolicy(iu);
  const role   = unitRole(iu);
  const n = slots.length;

  const gaps = [];
  slots.forEach((slot, i) => {
    slot.phases.gam.refreshIndex = i;
    if (i === 0) { slot.refreshFlag = 'init'; slot.refreshGapMs = null; return; }
    const gap = slot.timestamp - slots[i - 1].timestamp;
    slot.refreshGapMs = gap;
    gaps.push(gap);
    slot.refreshFlag = gap < policy.dupGapMs ? 'duplicate'
      : gap < policy.minRefreshMs ? 'rapid' : 'refresh';
  });

  const span = n > 1 ? slots[n - 1].timestamp - slots[0].timestamp : 0;
  const medianGap = gaps.length ? [...gaps].sort((a, b) => a - b)[Math.floor(gaps.length / 2)] : Infinity;
  const counts = { duplicate: 0, rapid: 0, refresh: 0, scroll: 0, flood: 0, gallery: 0 };
  // Each reason carries `evidence`:
  //   'deterministic' → provable (policy or structural). Drives HARD pass/fail.
  //   'heuristic'     → inferred from timing/pattern. Drives a REVIEW flag only,
  //                     never a silent pass and never an overconfident fail.
  const reasons = [];

  if (isGalleryPage(iu)) {
    // FNC `slide` (and any configured gallery page): the SAME iu is reused for
    // every swipe by design, so same-iu repeats are expected new-slide loads —
    // not refreshes, not duplicates. Deterministic from the iu structure (no
    // content_url or prev_scp needed), so this is a clean pass with no review.
    slots.forEach((s, i) => { if (i) s.refreshFlag = 'gallery'; });
    counts.gallery = n - 1;
  } else if (role === 'feed' && n >= policy.scrollMinRun && medianGap >= policy.scrollMinGapMs) {
    // Sustained, human-paced run of a recycled feed unit = infinite scroll.
    // Benign, but INFERRED — render as pass-inferred, no review needed.
    slots.forEach((s, i) => { if (i) s.refreshFlag = 'scroll'; });
    counts.scroll = n - 1;
  } else if (role === 'feed' && n >= policy.scrollMinRun && medianGap < policy.floodTightMs) {
    // Many feed calls at implausibly tight cadence — looks like a runaway loop,
    // but "flood" isn't a provable policy → REVIEW, not a hard fail.
    slots.forEach((s, i) => { if (i) s.refreshFlag = 'flood'; });
    counts.flood = n - 1;
    reasons.push({ severity: 'warn', evidence: 'heuristic', type: 'flood',
      detail: `${n} requests for feed unit in ${(span / 1000).toFixed(1)}s (median ${medianGap}ms) — possible runaway loop (review)` });
  } else if ((role === 'anchor' || role === 'unknown') && (n - 1) > policy.anchorFloodMax && span <= policy.floodWindowMs) {
    // Far more anchor requests than one slot + refreshes can justify. Strongly
    // suggests a flood, but still inferred (no placement id) → REVIEW.
    slots.forEach((s, i) => { if (i) s.refreshFlag = 'flood'; });
    counts.flood = n - 1;
    reasons.push({ severity: 'warn', evidence: 'heuristic', type: 'flood',
      detail: `${n} requests for one ${role} slot in ${(span / 1000).toFixed(1)}s — looks like a flood (expected ≤ ${policy.anchorFloodMax + 1}) (review)` });
  } else {
    // Normal path: tally the per-gap classification, splitting duplicates by
    // whether the correlator proves they came from one pageview.
    let provableDup = 0, inferredDup = 0;
    slots.forEach((s, i) => {
      if (i === 0) return;
      if (s.refreshFlag === 'duplicate') {
        const sameCorrelator = s.correlator && s.correlator === slots[i - 1].correlator;
        if (sameCorrelator) provableDup++; else inferredDup++;
      } else if (s.refreshFlag === 'rapid') counts.rapid++;
      else counts.refresh++;
    });
    counts.duplicate = provableDup + inferredDup;

    // Same correlator + same iu = one pageview requested the slot twice. Provable.
    if (provableDup) reasons.push({ severity: 'fail', evidence: 'deterministic', type: 'duplicate',
      detail: `${provableDup} duplicate(s) — same correlator (one pageview requested this slot twice)` });
    // Different correlators / no placement id — suspicious but not provable.
    if (inferredDup) reasons.push({ severity: 'warn', evidence: 'heuristic', type: 'duplicate',
      detail: `${inferredDup} call(s) < ${policy.dupGapMs}ms apart, different correlators — possible duplicate, no placement id to confirm (review)` });
    // Sub-30s reload of the SAME slot. A refresh is only legitimate at ≥30s, so
    // anything faster is a redundant EARLY RELOAD — NOT a refresh. For an anchor
    // (one slot per page, so consecutive calls are the same placement) that's a
    // deterministic redundant load. For a feed unit it might be a separate
    // placement, so it stays heuristic → review.
    if (counts.rapid) reasons.push({
      severity: 'warn',
      evidence: role === 'anchor' ? 'deterministic' : 'heuristic',
      type: 'early_reload',
      detail: role === 'anchor'
        ? `${counts.rapid} early reload(s) under the ${policy.minRefreshMs / 1000}s refresh minimum — redundant load, not a refresh`
        : `${counts.rapid} same-iu call(s) < ${policy.minRefreshMs / 1000}s apart — early reload or separate placement? (review)`,
    });
  }

  const detReasons  = reasons.filter(r => r.evidence === 'deterministic');
  const heurReasons = reasons.filter(r => r.evidence === 'heuristic');
  // HARD status comes from deterministic evidence only.
  const redundancyStatus = detReasons.some(r => r.severity === 'fail') ? 'fail'
    : detReasons.some(r => r.severity === 'warn') ? 'warn' : 'pass';
  const needsReview = heurReasons.length > 0;

  return { role, counts, reasons, detReasons, heurReasons, redundancyStatus, needsReview, span,
    medianGap: medianGap === Infinity ? null : medianGap };
}

/** Build the configurable context key for a lifecycle (content_url + cust keys). */
function contextKeyForLifecycle(lc) {
  const policy = redundancyPolicy(lc.adUnit || '');
  return policy.contextKeys
    .map(k => (k === 'content_url' ? (lc.contentUrl || '') : (lc.custParams?.[k] || '')))
    .join('|');
}

/* ─── slot grouper ──────────────────────────────────────────── */
// Groups flat per-GAM-request lifecycles by ad unit + visit + CONTEXT.
// Context (content_url + configured cust_params keys) keeps the same iu on
// different pages/screens from merging — even when content_url is identical and
// only a cust_param (e.g. pageName) differs.
export function groupLifecyclesBySlot(lifecycles) {
  const map = new Map();
  for (const lc of lifecycles) {
    const key = `${lc.adUnit || '?'}::${lc.visitKey || 'session'}::${contextKeyForLifecycle(lc)}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(lc);
  }
  return Array.from(map.entries()).map(([, slots]) => {
    slots.sort((a, b) => a.timestamp - b.timestamp);
    const adUnit = slots[0]?.adUnit || '?';

    const { role, counts, reasons, detReasons, heurReasons, redundancyStatus, needsReview, span, medianGap } = classifyContextSlots(slots, adUnit);

    const statuses = slots.map(s => s.overallStatus);
    // Per-request auction health (bids / impression / consent / sizes) — deterministic.
    const auctionStatus = statuses.includes('fail') ? 'fail' : statuses.includes('warn') ? 'warn' : 'pass';
    // HARD status = worst of auction + deterministic redundancy ONLY. Heuristic
    // findings never move this — they raise a separate "review" flag.
    const sev = { fail: 0, warn: 1, pass: 2 };
    const groupStatus = sev[auctionStatus] <= sev[redundancyStatus] ? auctionStatus : redundancyStatus;
    // Display tier adds a 'review' rung between warn and pass for inferred issues.
    const displayStatus = groupStatus !== 'pass' ? groupStatus : (needsReview ? 'review' : 'pass');
    // Build a human-readable visit label (e.g. "Visit 2 ↩ RETURNED")
    const firstSlot = slots[0];
    let visitLabel = null;
    if (firstSlot?.visitIdx != null) {
      const reasonIcon = { navigation: '→', returned: '↩', gap: '⏱' }[firstSlot.visitReason] || '';
      const reasonText = firstSlot.visitReason ? firstSlot.visitReason.toUpperCase() : '';
      visitLabel = `Visit ${firstSlot.visitIdx}${reasonIcon ? ` ${reasonIcon} ${reasonText}` : ''}`;
    }

    return {
      adUnit,
      slotLabel: firstSlot?.slotLabel || slotLabel(adUnit),
      sizes: firstSlot?.sizes || '',
      visitLabel,
      pageUrl: firstSlot?.pageUrl || null,
      contentUrl: firstSlot?.contentUrl || null,  // GAM content_url for page type detection
      pageName: firstSlot?.custParams?.pageName || null,
      visitIdx: firstSlot?.visitIdx ?? null,
      visitReason: firstSlot?.visitReason || null,
      groupStatus,                         // hard pass/warn/fail (deterministic only)
      displayStatus,                       // adds 'review' rung for inferred issues
      needsReview,                         // any heuristic finding present
      auctionStatus,
      redundancyStatus,
      unitRole: role,                      // 'anchor' | 'feed' | 'unknown'
      redundancyReasons: reasons,          // [{severity,evidence,type,detail}]
      reviewReasons: heurReasons,          // heuristic-only subset
      deterministicReasons: detReasons,
      redundancySpanMs: span,
      redundancyMedianGapMs: medianGap,
      refreshCount: slots.length - 1,      // total extra GAM calls after init
      legitRefreshCount: counts.refresh,   // gap ≥ 30s
      rapidRefreshCount: counts.rapid,     // 800ms ≤ gap < 30s
      duplicateCount: counts.duplicate,    // gap < 800ms (isolated)
      scrollCount: counts.scroll,          // recycled feed unit (scroll)
      floodCount: counts.flood,            // flood-flagged extra calls
      galleryCount: counts.gallery,        // gallery/carousel swipes (same iu by design)
      slots,
    };
  })
  .sort((a, b) => {
    // Primary: newest FIRST-load at top. Order by when each slot first loaded
    // (its Init time = earliest slot), not by its latest refresh — refreshes
    // stay inside the card and don't bump it up the list. slots[] is sorted
    // ascending, so slots[0] is the initial load.
    const aTs = a.slots[0]?.timestamp ?? 0;
    const bTs = b.slots[0]?.timestamp ?? 0;
    if (bTs !== aTs) return bTs - aTs;
    // Secondary: issues first (review ranks above a clean pass)
    const o = { fail: 0, warn: 1, review: 2, pass: 3 };
    return (o[a.displayStatus] ?? 4) - (o[b.displayStatus] ?? 4);
  });
}

/* ─── redundancy detector ───────────────────────────────────── */
// Analyses assembled lifecycles + raw rows to find wasteful / broken patterns.
// Returns array of findings sorted by severity (error first).
export function detectRedundancies(rows, lifecycles) {
  const findings = [];

  // ── 1+2. Duplicate / rapid-refresh / flood (context-aware) ─────
  // Derived from the SAME grouping the slot cards use, so the panel and the
  // badges never disagree. Each group has already classified its repeats using
  // the configurable context (content_url + cust keys) and the unit's role.
  for (const g of groupLifecyclesBySlot(lifecycles)) {
    for (const reason of (g.redundancyReasons || [])) {
      // Heuristic findings surface as 'review' (never 'error'); deterministic
      // ones map to error/warn. Keeps the panel honest about confidence.
      const severity = reason.evidence === 'heuristic' ? 'review'
        : reason.severity === 'fail' ? 'error' : 'warn';
      findings.push({
        type: reason.type,                                       // duplicate | rapid_refresh | flood
        severity,
        evidence: reason.evidence,
        slotLabel: g.slotLabel,
        visitLabel: g.visitLabel,
        detail: reason.detail,
        rowKeys: g.slots.map(s => s.phases?.gam?.rowKey).filter(Boolean),
      });
    }
  }

  // ── 3. Orphaned Prebid / APS requests ──────────────────────
  // An orphaned request is one that fired but was never claimed by any lifecycle.
  // Wait 2 s from the request timestamp before flagging — the matching GAM call
  // may still be in flight (APS/Prebid fire ~200ms–2s before the GAM request).
  const now = Date.now();
  const ORPHAN_SETTLE_MS = 2000; // wait before declaring an orphan

  const matchedPrebidKeys = new Set(
    lifecycles.map(lc => lc.phases.prebid?.rowKey).filter(Boolean)
  );
  rows
    .filter(r => ((r && (r.type || r.requestType)) || '').toLowerCase() === 'prebid')
    .forEach(pr => {
      if (matchedPrebidKeys.has(pr._k)) return;
      // Only flag once the request is settled (> 2 s old)
      if (now - ts(pr) < ORPHAN_SETTLE_MS) return;
      const label = pr.validData?.summary?.adUnitCode
        || pr.validData?.adUnit
        || pr.validData?.summary?.code
        || '?';
      findings.push({
        type: 'orphaned_prebid',
        severity: 'warn',
        slotLabel: slotLabel(label),
        visitLabel: pr._visitIdx != null ? `Visit ${pr._visitIdx}` : null,
        detail: 'Prebid auction ran but no GAM call followed — wasted header bidding round-trip',
        rowKeys: [pr._k].filter(k => k != null),
      });
    });

  // ── 3b. Orphaned APS request (no lifecycle claimed it) ─────
  const matchedAmazonKeys = new Set(
    lifecycles.map(lc => lc.phases.amazon?.rowKey).filter(Boolean)
  );
  const amazonTypes3b = new Set(['amazon', 'amazon_video']);
  rows
    .filter(r => amazonTypes3b.has(((r && (r.type || r.requestType)) || '').toLowerCase()))
    .forEach(ar => {
      if (matchedAmazonKeys.has(ar._k)) return;
      // Only flag once settled
      if (now - ts(ar) < ORPHAN_SETTLE_MS) return;
      const slots = ar.validData?.expanded?.slots || [];
      const szList = slots.map(s => s.sz || '?').join(', ') || '?';
      findings.push({
        type: 'orphaned_amazon',
        severity: 'warn',
        slotLabel: 'Amazon APS',
        visitLabel: ar._visitIdx != null ? `Visit ${ar._visitIdx}` : null,
        detail: `Amazon APS request (sizes: ${szList}) fired but no GAM slot claimed it — possible redundant bid request`,
        rowKeys: [ar._k].filter(k => k != null),
      });
    });

  // ── 3c. Count mismatch: Prebid/APS > GAM requests ──────────
  // Settled rows only (> 2 s old) to avoid false positives on in-flight auctions
  const settledPrebidCount = rows.filter(r =>
    ((r && (r.type || r.requestType)) || '').toLowerCase() === 'prebid' && now - ts(r) >= ORPHAN_SETTLE_MS
  ).length;
  const settledAmazonCount = rows.filter(r =>
    amazonTypes3b.has(((r && (r.type || r.requestType)) || '').toLowerCase()) && now - ts(r) >= ORPHAN_SETTLE_MS
  ).length;
  const gamCount = lifecycles.length;

  if (settledPrebidCount > gamCount && gamCount > 0) {
    findings.push({
      type: 'prebid_gam_mismatch',
      severity: 'warn',
      slotLabel: 'Prebid',
      visitLabel: null,
      detail: `${settledPrebidCount} Prebid auctions vs ${gamCount} GAM requests — ${settledPrebidCount - gamCount} Prebid auction(s) have no corresponding GAM call`,
      rowKeys: [],
    });
  }
  if (settledAmazonCount > gamCount && gamCount > 0) {
    findings.push({
      type: 'amazon_gam_mismatch',
      severity: 'warn',
      slotLabel: 'Amazon APS',
      visitLabel: null,
      detail: `${settledAmazonCount} APS requests vs ${gamCount} GAM requests — ${settledAmazonCount - gamCount} APS request(s) have no corresponding GAM slot`,
      rowKeys: [],
    });
  }

  // ── 4. Double impression — THIS slot's ad counted twice by the same party ──
  // Proven duplicates only: two or more impression rows of the SAME type that
  // carry one of this slot's own beacon keys (xai/sai recovered from its GAM
  // response), or its linked beacon URL fired more than once. This used to be a
  // 60s time window over every impression row, which counted other slots'
  // beacons and a mediation win's two parties (Google's ping + Liftoff's own
  // tracker) as one slot double-counting — an error on correct traffic.
  // Third way to prove it: the SDK firing the slot's impression twice at once
  // (the bug this check exists for) — another beacon of the same type to the same
  // endpoint within SIMULTANEOUS_IMP_MS of the slot's linked one, that no other
  // slot claims. Those often differ by a cache-buster and share no xai. A beacon
  // claimed by a TWIN request (same ad unit, GAM fired within the same window —
  // two ad requests at once, each rendering) still counts: that is two
  // impressions for one placement.
  const impressionTypes = new Set([
    'google_impression', 'omid_impression', 'amazon_impression',
    'prebid_win', 'bidder_impression', 'impression',
  ]);
  // ActiveView pings are measurement, not impressions (stale saved type google_impression included).
  const impRows = rows.filter(r => impressionTypes.has(((r && (r.type || r.requestType)) || '').toLowerCase()) && !isActiveViewRow(r));
  const urlParam = (r, k) => { try { return new URL((r && r.url) || '').searchParams.get(k); } catch { return null; } };
  const typeOfImp = (r) => ((r && (r.type || r.requestType)) || '').toLowerCase();
  const seenDouble = new Set();
  const SIMULTANEOUS_IMP_MS = 2000;
  const endpointOf = (r) => { try { const u = new URL((r && r.url) || ''); return u.host + u.pathname; } catch { return null; } };
  // Impression rows another slot owns — never charged to this one.
  const impOwner = new Map();
  lifecycles.forEach(o => {
    const k = o && o.phases && o.phases.impression ? o.phases.impression.rowKey : null;
    if (k != null && !impOwner.has(k)) impOwner.set(k, o);
  });

  lifecycles.forEach(lc => {
    const ph = (lc && lc.phases) || {};
    const ex = (ph.gam && ph.gam.row && ph.gam.row.validData && ph.gam.row.validData.expanded) || {};
    const xais = new Set([ex.impXai, ...(Array.isArray(ex.impXaiAll) ? ex.impXaiAll : [])].filter(Boolean));
    const sais = new Set([ex.impSai, ...(Array.isArray(ex.impSaiAll) ? ex.impSaiAll : [])].filter(Boolean));
    const linkedKey = ph.impression ? ph.impression.rowKey : null;
    const linked = linkedKey != null ? impRows.find(r => r._k === linkedKey) : null;
    const linkedUrl = (linked && linked.url) || null;
    if (!xais.size && !sais.size && !linkedUrl) return;
    const linkedEndpoint = linked ? endpointOf(linked) : null;
    const linkedTs = linked ? ts(linked) : null;
    const linkedType = linked ? typeOfImp(linked) : null;
    const myUnit = ph.gam ? ph.gam.adUnit : null;
    const myGamTs = ph.gam && ph.gam.row ? ts(ph.gam.row) : null;
    const isTwin = (o) => {
      const og = o && o.phases && o.phases.gam;
      return !!(og && myUnit && og.adUnit === myUnit && og.row && myGamTs != null
        && Math.abs(ts(og.row) - myGamTs) <= SIMULTANEOUS_IMP_MS);
    };

    const byType = new Map();
    impRows.forEach(r => {
      const x = urlParam(r, 'xai');
      const s = urlParam(r, 'sai');
      const owner = impOwner.get(r._k);
      const simultaneous = linked && r !== linked && (owner == null || owner === lc || isTwin(owner))
        && typeOfImp(r) === linkedType && linkedEndpoint && endpointOf(r) === linkedEndpoint
        && Math.abs(ts(r) - linkedTs) <= SIMULTANEOUS_IMP_MS;
      const via = (x && xais.has(x)) ? 'xai' : (s && sais.has(s)) ? 'sai'
        : (linkedUrl && r.url === linkedUrl) ? 'beacon URL'
        : (r === linked || simultaneous) ? 'endpoint, fired together' : null;
      if (!via) return;
      const t = typeOfImp(r);
      if (!byType.has(t)) byType.set(t, { rows: [], via });
      const g = byType.get(t);
      g.rows.push(r);
      if (r !== linked) g.via = via;   // describe why the EXTRA beacon counts, not the slot's own
    });

    byType.forEach(({ rows: fired, via }, t) => {
      if (fired.length < 2) return;
      const keys = fired.map(r => r._k).filter(k => k != null);
      const sig = t + ':' + keys.slice().sort().join('|');
      if (seenDouble.has(sig)) return;
      seenDouble.add(sig);
      findings.push({
        type: 'double_impression',
        severity: 'error',
        slotLabel: lc.slotLabel,
        visitLabel: lc.visitIdx != null ? `Visit ${lc.visitIdx}` : null,
        detail: `${fired.length} ${t} beacons for this slot's ad (same ${via}) — risk of double-counting revenue`,
        rowKeys: keys,
      });
    });
  });

  // ── 5. Duplicate Amazon requests for same slot+visit ────────
  const amazonTypes = new Set(['amazon', 'amazon_video']);
  const amzRows = rows.filter(r => amazonTypes.has(((r && (r.type || r.requestType)) || '').toLowerCase()));
  // Group by visitKey proximity — Amazon requests within 500ms of each other
  const seenAmz = [];
  amzRows
    .sort((a, b) => ts(a) - ts(b))
    .forEach(r => {
      const rts = ts(r);
      const dup = seenAmz.find(s => Math.abs(ts(s) - rts) < 500 && s._visitKey === r._visitKey);
      if (dup) {
        findings.push({
          type: 'duplicate_amazon',
          severity: 'warn',
          slotLabel: 'Amazon APS',
          visitLabel: r._visitIdx != null ? `Visit ${r._visitIdx}` : null,
          detail: `Amazon APS fired ${Math.abs(ts(dup) - rts)}ms apart on same visit — duplicate bid request`,
          rowKeys: [dup._k, r._k].filter(k => k != null),
        });
      } else {
        seenAmz.push(r);
      }
    });

  // Sort: errors first, then warnings, then review (inferred); stable within
  findings.sort((a, b) => {
    const rank = { error: 0, warn: 1, review: 2 };
    return (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3);
  });

  return findings;
}

/* ─── redundancy panel UI ───────────────────────────────────── */
const REDUNDANCY_META = {
  duplicate_gam:       { icon: '🔁', label: 'Duplicate GAM call' },
  duplicate:           { icon: '🔁', label: 'Duplicate GAM call' },
  flood:               { icon: '🌊', label: 'Request flood' },
  early_reload:        { icon: '⚡', label: 'Early reload (<30s)' },
  rapid_refresh:       { icon: '⚡', label: 'Early reload (<30s)' },
  orphaned_prebid:     { icon: '👻', label: 'Orphaned Prebid' },
  orphaned_amazon:     { icon: '👻', label: 'Orphaned APS' },
  prebid_gam_mismatch: { icon: '⚠️', label: 'Prebid/GAM mismatch' },
  amazon_gam_mismatch: { icon: '⚠️', label: 'APS/GAM mismatch' },
  double_impression:   { icon: '💥', label: 'Double impression' },
  duplicate_amazon:    { icon: '🔁', label: 'Duplicate Amazon' },
};

/* Redundancy pill + floating popover — lives inline in the summary bar,
   pops a sheet downward on click. No panel above the slot cards. */
export function RedundancyPopover({ findings, onJumpTo }) {
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const ref = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!findings.length) return null;

  const errors = findings.filter(f => f.severity === 'error').length;
  const warns  = findings.filter(f => f.severity === 'warn').length;
  const reviews = findings.filter(f => f.severity === 'review').length;
  const pillColor = errors > 0 ? STATUS_COLOR.fail : warns > 0 ? STATUS_COLOR.warn : STATUS_COLOR.review;

  // Group by type + slot
  const groups = [];
  const groupMap = {};
  findings.forEach(f => {
    const key = `${f.type}__${f.slotLabel || ''}`;
    if (groupMap[key] === undefined) {
      groupMap[key] = groups.length;
      groups.push({ key, type: f.type, slotLabel: f.slotLabel, severity: f.severity, items: [] });
    }
    if (f.severity === 'error') groups[groupMap[key]].severity = 'error';
    groups[groupMap[key]].items.push(f);
  });

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Pill trigger */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 9px', borderRadius: 12, cursor: 'pointer',
          fontSize: 11, fontWeight: 700,
          background: pillColor.bg, color: pillColor.text,
          border: `1px solid ${pillColor.border}`,
          outline: 'none',
        }}
      >
        ⚠ {groups.length} issue{groups.length !== 1 ? 's' : ''}
        {errors > 0 && <span style={{ marginLeft: 2 }}>· {errors}✗</span>}
        {warns  > 0 && <span style={{ marginLeft: 2 }}>· {warns}⚠</span>}
        {reviews > 0 && <span style={{ marginLeft: 2 }}>· {reviews}🔍</span>}
        <span style={{ fontSize: 9, marginLeft: 2 }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Floating sheet */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 9999,
          width: 380, maxHeight: 420, overflowY: 'auto',
          background: 'var(--card)',
          border: '1px solid var(--line)',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
        }}>
          {/* Sheet header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderBottom: '1px solid var(--line)',
            position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1,
          }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink)' }}>
              ⚠ Redundant / Wasteful Requests
            </span>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)', lineHeight: 1 }}>✕</button>
          </div>

          {/* Grouped findings */}
          {groups.map((g, gi) => {
            const meta = REDUNDANCY_META[g.type] || { icon: '⚠', label: g.type };
            const sc   = g.severity === 'error' ? STATUS_COLOR.fail : g.severity === 'review' ? STATUS_COLOR.review : STATUS_COLOR.warn;
            const isExpanded = !!expandedGroups[g.key];
            const count = g.items.length;
            const first = g.items[0];
            return (
              <div key={g.key} style={{ borderBottom: gi < groups.length - 1 ? '1px solid var(--line)' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px' }}>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 5, flexShrink: 0, marginTop: 1,
                    background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                  }}>
                    {g.severity === 'error' ? '✗' : '⚠'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--ink)' }}>
                        {meta.icon} {meta.label}
                      </span>
                      {g.slotLabel && (
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{g.slotLabel}</span>
                      )}
                      {count > 1 && (
                        <span
                          onClick={() => toggleGroup(g.key)}
                          style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 9,
                            background: 'var(--line)', color: 'var(--muted)',
                            cursor: 'pointer', border: '1px solid var(--line)' }}
                        >
                          ×{count} {isExpanded ? '▲' : '▼'}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      {first.detail}{count > 1 && !isExpanded ? ` (+ ${count - 1} more)` : ''}
                    </div>
                    {isExpanded && count > 1 && (
                      <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid var(--line)' }}>
                        {g.items.map((f, fi) => (
                          <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 11, color: 'var(--muted)' }}>
                            <span style={{ minWidth: 14, color: 'var(--muted)' }}>{fi + 1}.</span>
                            <span style={{ flex: 1 }}>{f.detail}</span>
                            {f.rowKeys?.[0] && onJumpTo && (
                              <button onClick={() => { onJumpTo(f.rowKeys[0]); setOpen(false); }}
                                style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, cursor: 'pointer',
                                  border: '1px solid var(--line)', background: 'transparent', color: 'var(--muted)' }}>→</button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Jump to first */}
                  {first.rowKeys?.length > 0 && onJumpTo && (
                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                      {first.rowKeys.slice(0, 2).map((rk, ri) => (
                        <button key={ri} onClick={() => { onJumpTo(rk); setOpen(false); }}
                          style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, cursor: 'pointer',
                            border: `1px solid ${sc.border}`, background: 'transparent', color: sc.text, fontWeight: 600 }}>
                          → {ri === 0 ? 'Req' : 'Dup'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── WP6: orphan / duplicate summary popover (spec §WP6) ──────────────
 * Mirrors RedundancyPopover's pill-trigger + floating-sheet pattern —
 * same open/close-on-outside-click behavior, same STATUS_COLOR palette. */
export function OrphanPopover({ report, onJumpTo }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const orphanCount = report?.counts?.orphans ?? report?.orphans?.length ?? 0;
  const dupCount = report?.counts?.duplicates ?? report?.duplicates?.length ?? 0;
  if (orphanCount === 0 && dupCount === 0) return null; // nothing to show — subtle no-op

  const pillColor = orphanCount > 0 ? STATUS_COLOR.warn : STATUS_COLOR.review;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '3px 9px', borderRadius: 12, cursor: 'pointer',
          fontSize: 11, fontWeight: 700,
          background: pillColor.bg, color: pillColor.text,
          border: `1px solid ${pillColor.border}`,
          outline: 'none',
        }}
      >
        {orphanCount > 0 && <span>⚠ {orphanCount} orphaned</span>}
        {orphanCount > 0 && dupCount > 0 && <span>·</span>}
        {dupCount > 0 && <span>{dupCount} duplicate{dupCount !== 1 ? 's' : ''}</span>}
        <span style={{ fontSize: 9, marginLeft: 2 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 9999,
          width: 380, maxHeight: 420, overflowY: 'auto',
          background: 'var(--card)',
          border: '1px solid var(--line)',
          borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.28)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderBottom: '1px solid var(--line)',
            position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1,
          }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ink)' }}>
              ⚠ Orphaned / Duplicate Requests
            </span>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--muted)', lineHeight: 1 }}>✕</button>
          </div>

          {report.reasons?.length > 0 && (
            <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {report.reasons.map((r, i) => (
                <span key={i} style={{ fontSize: 10.5, color: 'var(--muted)' }}>{r}</span>
              ))}
            </div>
          )}

          {report.orphans?.length > 0 && (
            <div style={{ padding: '6px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: STATUS_COLOR.warn.text, textTransform: 'uppercase', marginBottom: 4 }}>
                Orphans ({report.orphans.length})
              </div>
              {report.orphans.map((o, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 0' }}>
                  <span style={{ flex: 1, minWidth: 0, color: 'var(--ink)' }}>
                    {o.type || 'row'}{o.reason ? ` — ${o.reason}` : ''}
                  </span>
                  {onJumpTo && o.rowKey != null && (
                    <button onClick={() => { onJumpTo(o.rowKey); setOpen(false); }}
                      style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, cursor: 'pointer',
                        border: `1px solid ${STATUS_COLOR.warn.border}`, background: 'transparent', color: STATUS_COLOR.warn.text, fontWeight: 600, flexShrink: 0 }}>
                      → Req
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {report.duplicates?.length > 0 && (
            <div style={{ padding: '6px 12px', borderTop: report.orphans?.length > 0 ? '1px solid var(--line)' : 'none' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: STATUS_COLOR.review.text, textTransform: 'uppercase', marginBottom: 4 }}>
                Duplicates ({report.duplicates.length})
              </div>
              {report.duplicates.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '3px 0' }}>
                  <span style={{ flex: 1, minWidth: 0, color: 'var(--ink)' }}>
                    {d.type || 'row'}{d.reason ? ` — ${d.reason}` : ''}
                  </span>
                  {onJumpTo && d.rowKey != null && (
                    <button onClick={() => { onJumpTo(d.rowKey); setOpen(false); }}
                      style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, cursor: 'pointer',
                        border: `1px solid ${STATUS_COLOR.review.border}`, background: 'transparent', color: STATUS_COLOR.review.text, fontWeight: 600, flexShrink: 0 }}>
                      → Req
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── SDK version reference (as of May 2026) ───────────────── */
// GMA SDK uses date-based versioning: sdk_YYYYMMDD_RCxx (e.g. sdk_20260427_RC00)
// OMID partner string: {version}-google_{date} (e.g. 1.5.2-google_20241009)
const SDK_VERSIONS = {
  gma_ios:        { label: 'GMA SDK (iOS)',            latest: 'sdk_20260427_RC00', platform: 'iOS',     ref: 'https://developers.google.com/admob/ios/rel-notes' },
  gma_android:    { label: 'GMA SDK (Android)',         latest: 'sdk_20260427_RC00', platform: 'Android', ref: 'https://developers.google.com/admob/android/rel-notes' },
  prebid_ios:     { label: 'Prebid Mobile (iOS)',       latest: '3.3.1',             platform: 'iOS',     ref: 'https://github.com/prebid/prebid-mobile-ios/releases' },
  prebid_android: { label: 'Prebid Mobile (Android)',   latest: '3.3.1',             platform: 'Android', ref: 'https://github.com/prebid/prebid-mobile-android/releases' },
  amazon_aps:     { label: 'Amazon APS SDK (Android)',  latest: '9.4.2',             platform: 'Android', ref: 'https://github.com/aps-mobile-engineering/aps-android-sdk' },
  omid:           { label: 'OMID (IAB / Google)',       latest: '1.5.2-google_20241009', platform: 'Both', ref: 'https://iabtechlab.com/standards/open-measurement-sdk/' },
};

/* ─── SDK compliance rules ─────────────────────────────────── */
// Each rule: { key, label, platform, latest, ref, get(rows)→string|null, check(val)→bool, expect }
const SDK_RULES = [
  {
    key: 'gma_sdk', label: 'GMA SDK', platform: 'iOS/Android',
    latest: 'sdk_20260427_RC00',
    ref: 'https://developers.google.com/admob/ios/rel-notes',
    get: (rows) => {
      // jsv param in GAM request → e.g. "sdk_20260427_RC00"
      const r = rows.find(r => r.validData?.expanded?.meta?.sdk);
      return r?.validData?.expanded?.meta?.sdk || null;
    },
    // Pass if sdk_ present; warn if the date is stale (>180 days behind latest)
    check: (v) => {
      if (!v) return false;
      const m = String(v).match(/sdk_(\d{4})(\d{2})(\d{2})/i);
      if (!m) return !!v; // present but unusual format → pass
      const sdkDate = new Date(`${m[1]}-${m[2]}-${m[3]}`);
      const latestDate = new Date('2026-04-27');
      return (latestDate - sdkDate) <= 180 * 86400000; // within 6 months
    },
    expect: 'jsv param in GAM (e.g. sdk_20260427_RC00)',
  },
  {
    key: 'prebid_sdk', label: 'Prebid Mobile SDK', platform: 'iOS/Android',
    latest: '3.3.1',
    ref: 'https://github.com/prebid/prebid-mobile-ios/releases',
    get: (rows) => {
      const r = rows.find(r => (r.type || r.requestType || '') === 'prebid');
      // prebid version sometimes in app.prebid or request summary
      return r?.validData?.summary?.prebid || r?.validData?.expanded?.meta?.prebidVer || null;
    },
    check: v => !!v,
    expect: 'app.prebid field in Prebid request',
  },
  {
    key: 'amazon_sdk', label: 'Amazon APS SDK', platform: 'Android',
    latest: '9.4.2',
    ref: 'https://github.com/aps-mobile-engineering/aps-android-sdk',
    get: (rows) => {
      // amzn_b bid token in GAM cust_params signals APS is integrated
      // (validData.cust is not persisted — parse directly from URL)
      const r = rows.find(r => {
        const rt = (r.type || r.requestType || '').toLowerCase();
        if (rt !== 'google') return false;
        const cust = parseCustParams(r.url || '');
        return !!(cust.amzn_b || cust.amznslots);
      });
      if (!r) return null;
      const cust = parseCustParams(r.url || '');
      const slot = cust.amznslots ? cust.amznslots.split('%')[0] : null;
      return slot ? `amzn_b present · slot ${slot}` : 'amzn_b present';
    },
    check: v => !!v,
    expect: 'amzn_b token in GAM cust_params',
  },
  {
    key: 'omid', label: 'OMID', platform: 'iOS/Android',
    latest: '1.5.2-google_20241009',
    ref: 'https://iabtechlab.com/standards/open-measurement-sdk/',
    get: (rows) => {
      // omid_p param in GAM → e.g. "1.5.2-google_20241009"
      const r = rows.find(r => r.validData?.expanded?.meta?.omidPartner);
      return r?.validData?.expanded?.meta?.omidPartner || null;
    },
    // Pass if major.minor ≥ 1.3
    check: (v) => {
      if (!v) return false;
      const m = String(v).match(/^(\d+)\.(\d+)/);
      if (!m) return true; // present, unusual format
      return Number(m[1]) > 1 || (Number(m[1]) === 1 && Number(m[2]) >= 3);
    },
    expect: 'omid_p in GAM request (≥1.3 required)',
  },
  {
    key: 'att', label: 'ATT status', platform: 'iOS',
    latest: 'att=3 (authorized)',
    ref: 'https://developer.apple.com/documentation/apptrackingtransparency',
    get: (rows) => {
      // ATT comes from Prebid request issues (code: 'att') stamped by the proxy
      const prebidRow = rows.find(r => (r.type || r.requestType || '').toLowerCase() === 'prebid');
      if (!prebidRow) return null;
      // Try issues array first
      const attIssue = (prebidRow.issues || []).find(i => i.code === 'att' || String(i.code).toLowerCase().includes('att'));
      if (attIssue?.note) {
        const m = String(attIssue.note).match(/ATT[=\s:]+(\d)/i);
        const val = m ? Number(m[1]) : null;
        if (val != null) {
          const labels = { 0: 'att=0 · Not Determined', 1: 'att=1 · Restricted', 2: 'att=2 · Denied ⚠', 3: 'att=3 · Authorized ✓' };
          return labels[val] || `att=${val}`;
        }
        return attIssue.note.slice(0, 60);
      }
      // Fallback: check device.att in expanded data
      const att = prebidRow.validData?.expanded?.device?.att;
      if (att != null) {
        const labels = { 0: 'att=0 · Not Determined', 1: 'att=1 · Restricted', 2: 'att=2 · Denied ⚠', 3: 'att=3 · Authorized ✓' };
        return labels[att] || `att=${att}`;
      }
      return null;
    },
    check: v => v != null && (v.includes('Authorized') || v.includes('att=3')),
    expect: 'att=3 required for IDFA access',
  },
  {
    key: 'gpp', label: 'GPP / gpp_sid', platform: 'Both',
    latest: 'Required (IAB 2023+)',
    ref: 'https://iabtechlab.com/standards/global-privacy-platform/',
    get: (rows) => {
      const r = rows.find(r => (r.type || r.requestType || '') === 'google');
      const q = r?.validData?.cust || {};
      return q.gpp || q.gpp_sid || null;
    },
    check: v => !!v,
    expect: 'gpp/gpp_sid in cust_params (replaces us_privacy)',
  },
  {
    key: 'hb_cache_id', label: 'Prebid cache ID', platform: 'Both',
    latest: 'Required for Prebid → GAM',
    ref: 'https://docs.prebid.org/adops/step-by-step.html',
    get: (rows) => {
      const r = rows.find(r => (r.type || r.requestType || '') === 'google' && r.validData?.expanded?.prebidId);
      return r ? r.validData.expanded.prebidId : null;
    },
    check: v => !!v,
    expect: 'hb_cache_id in GAM cust_params',
  },
  {
    key: 'schain', label: 'SupplyChain', platform: 'Both',
    latest: 'Required (OpenRTB 2.5+)',
    ref: 'https://iabtechlab.com/standards/supply-chain-object/',
    get: (rows) => {
      const r = rows.find(r => (r.type || r.requestType || '') === 'prebid');
      if (!r) return null;
      const ok = r.issues?.some(i => i.code === 'schain' && i.level === 'pass');
      return ok ? 'present ✓' : null;
    },
    check: v => !!v,
    expect: 'source.schain in Prebid request',
  },
];

// Semi-transparent colors so cards look right in both light and dark mode.
// border = saturated accent; bg = very subtle tint; text = readable on dark bg.
const STATUS_COLOR = {
  pass: { bg: 'rgba(93, 202, 165, 0.08)',  border: '#3DAA85', text: '#4DC99A', badge: '#1D6E50' },
  review: { bg: 'rgba(99, 179, 237, 0.10)', border: '#3F86C7', text: '#63B3ED', badge: '#1C4E7A' },
  warn: { bg: 'rgba(239, 159, 39, 0.10)',  border: '#CC8A20', text: '#EF9F27', badge: '#8A5A10' },
  fail: { bg: 'rgba(240, 100, 100, 0.10)', border: '#C05050', text: '#E07070', badge: '#8A2525' },
  miss: { bg: 'transparent', border: 'var(--line)', text: 'var(--muted)', badge: 'var(--line)' },
};

/* Match confidence (exact / strong / weak): utils/linkConfidence.js + components/LinkConfidence.jsx. */

/* ─── WP6: proven|inferred|guessed tier badge ─────────────────────
 * Distinct from ConfidenceChip (exact/strong/weak, derived from raw
 * matchTier strings). This one renders the WP2 `phases.<phase>.tier`
 * field directly (spec §3's canonical 3-level vocabulary) so the two
 * confidence systems stay visually related but not conflated. */
export const TIER_META = {
  proven:   { label: 'Proven',   dot: '●', color: '#1D9E75', bg: 'rgba(93,202,165,0.12)', border: '#3DAA85',
              desc: 'proven — matched by hard token (correlator / xai / sai / sigh / amzn_b / cache id / exact nurl)' },
  inferred: { label: 'Inferred', dot: '◐', color: '#CC8A20', bg: 'rgba(239,159,39,0.12)', border: '#EF9F27',
              desc: 'inferred — soft signal + timing (gpid / bidder price / prebid size / a3p buyer / adapter)' },
  guessed:  { label: 'Guessed',  dot: '○', color: '#6b7280', bg: 'rgba(107,114,128,0.10)', border: '#9ca3af',
              desc: 'guessed — timing / ad-unit proximity only, no structural signal — verify manually' },
};
export function TierBadge({ tier, compact = false, style = {} }) {
  if (!tier) return null;
  const m = TIER_META[tier];
  if (!m) return null;
  const title = m.desc;
  if (compact) {
    return (
      <span title={title} style={{
        display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
        background: m.color, border: `1px solid ${m.border}`, flexShrink: 0, ...style,
      }} />
    );
  }
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      fontSize: 9.5, fontWeight: 700, lineHeight: '14px',
      padding: '1px 6px', borderRadius: 10,
      color: m.color, background: m.bg, border: `1px solid ${m.border}`,
      whiteSpace: 'nowrap', ...style,
    }}>
      {m.dot} {m.label}
    </span>
  );
}

/* ─── SDK compliance panel ─────────────────────────────────── */
export function SDKPanel({ rows }) {
  const [open, setOpen] = React.useState(false);
  const [showRef, setShowRef] = React.useState(false);
  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      {/* Collapsed header — click to expand */}
      <div
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ fontSize: 10, color: 'var(--muted)' }}>{open ? '▲' : '▼'}</span>
        <p style={{ ...S.sectionLabel, margin: 0, fontSize: 11, opacity: 0.7 }}>SDK &amp; Signal Compliance</p>
        {!open && <span style={{ fontSize: 10, color: 'var(--muted)', opacity: 0.5 }}>click to expand</span>}
        {open && (
          <button
            onClick={e => { e.stopPropagation(); setShowRef(v => !v); }}
            style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: '1px solid var(--line)',
              background: 'transparent', cursor: 'pointer', color: 'var(--muted)', marginLeft: 4 }}
          >
            {showRef ? 'Hide versions' : 'Version reference'}
          </button>
        )}
      </div>
      {!open && <div />}
      {open && <div style={{ marginTop: 10 }}>

      {/* Version reference table */}
      {showRef && (
        <div style={{ marginBottom: 12, padding: '8px 10px', borderRadius: 6,
          background: 'var(--surface)', fontSize: 11 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '3px 14px', alignItems: 'center' }}>
            <span style={S.colHead}>SDK</span>
            <span style={S.colHead}>Platform</span>
            <span style={S.colHead}>Latest (May 2026)</span>
            <span style={S.colHead}>Docs</span>
            {Object.entries(SDK_VERSIONS).map(([k, v]) => (
              <React.Fragment key={k}>
                <span style={{ color: 'var(--ink)', padding: '2px 0', fontWeight: 500 }}>{v.label}</span>
                <span style={{ color: 'var(--muted)', padding: '2px 0' }}>{v.platform}</span>
                <span style={{ color: '#0F6E56', padding: '2px 0', fontWeight: 600 }}>{v.latest}</span>
                <a href={v.ref} target="_blank" rel="noreferrer"
                  style={{ color: 'var(--primary)', textDecoration: 'none', padding: '2px 0' }}>↗</a>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Per-signal status rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header row */}
        <div style={{ display: 'grid', gridTemplateColumns: '160px 66px 1fr', gap: '0 10px', fontSize: 10,
          padding: '0 6px 4px', borderBottom: '1px solid var(--line)' }}>
          <span style={S.colHead}>Signal</span>
          <span style={{ ...S.colHead, textAlign: 'center' }}>Status</span>
          <span style={S.colHead}>Observed</span>
        </div>
        {SDK_RULES.map(rule => {
          const val = rule.get(rows);
          const detected = val != null;
          const ok  = detected && rule.check(val);
          // miss = not captured yet; fail = captured but wrong value
          const sc  = !detected ? STATUS_COLOR.miss : ok ? STATUS_COLOR.pass : STATUS_COLOR.fail;
          const statusLabel = !detected ? '— n/a' : ok ? '✓ pass' : '✗ fail';
          return (
            <div key={rule.key} style={{
              display: 'grid', gridTemplateColumns: '160px 66px 1fr', gap: '0 10px',
              padding: '5px 6px', borderRadius: 5, fontSize: 11,
              background: !detected ? 'transparent' : ok ? 'transparent' : 'rgba(240,100,100,0.05)',
              alignItems: 'start',
            }}>
              <div>
                <div style={{ color: 'var(--ink)', fontWeight: 500, lineHeight: '14px' }}>{rule.label}</div>
                <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 1 }}>{rule.platform}</div>
              </div>
              <div style={{ textAlign: 'center', paddingTop: 1 }}>
                <span style={{
                  display: 'inline-block', fontSize: 10, fontWeight: 700,
                  padding: '2px 6px', borderRadius: 8,
                  background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                  whiteSpace: 'nowrap',
                }}>
                  {statusLabel}
                </span>
              </div>
              <div style={{ overflow: 'hidden', paddingTop: 1 }}>
                {detected
                  ? <>
                      <span style={{ color: sc.text, fontWeight: 500, fontSize: 11 }}>{String(val).slice(0, 60)}</span>
                      {rule.latest && <span style={{ color: 'var(--muted)', marginLeft: 8, fontSize: 10 }}>· target: {rule.latest}</span>}
                    </>
                  : <>
                      <span style={{ color: 'var(--muted))', fontStyle: 'italic', fontSize: 10 }}>Not detected</span>
                      {rule.expect && <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: 10 }}>({rule.expect})</span>}
                    </>
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>}
    </div>
  );
}

// Debounce before POSTing the session to /proxy/lifecycle. Sized for a filter
// toggle, not a network round trip: long enough that clicking through a few
// filters sends one request instead of several multi-megabyte ones, short
// enough that a deliberate change still feels immediate.
//
// SHARED (WP5, docs/frontend-validation-port-spec.md): all three POST-on-a-
// debounce views (this one, VideoLifecycleView.jsx, BidHealthView.jsx) used
// to inline this same `400` independently — a classic drift trap where one
// gets tuned and the others silently don't. Exported so the other two import
// it instead of re-inlining the literal.
export const LIFECYCLE_POST_DEBOUNCE_MS = 400;

/* ─── shared styles ────────────────────────────────────────── */
const S = {
  chip: {
    fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500,
    background: 'var(--surface)',
    color: 'var(--muted)',
    border: '0.5px solid var(--line)',
  },
  muted: { fontSize: 12, color: 'var(--muted)', fontStyle: 'italic' },
  sectionLabel: { fontSize: 10, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' },
  colHead: { fontSize: 10, fontWeight: 500, color: 'var(--muted)', padding: '3px 0', borderBottom: '0.5px solid var(--line)' },
};
